import type { LocationSearchHit } from "@/types/place"
import type { VoiceChatEvent, VoiceChatEventData } from "@/types/socket"
import type {
  VoiceCutoffReason,
  VoiceHistoryItem,
  VoiceLiveEntry,
  VoiceSkillResponseConfiguration,
  VoiceToolResult,
  VoiceTranscriptPersistenceResult,
} from "@/types/voice-chat"
import {
  VOICE_AUDIO_SYSTEM_DEVICE_ID,
  type ReconcileRunningVoiceToolCall,
  type VoiceChatRTCDeps,
  type VoiceFinalizedTranscriptTurn,
  type VoicePlaceAnnotationCandidate,
  type VoiceRealtimeClientEvent,
  type VoiceRealtimeEvent,
  type VoiceRealtimeFunctionTool,
  type VoiceRealtimeResponseInputItem,
  type VoiceResponseCreateTrigger,
  type VoiceResponseMetadata,
  type VoiceSessionStateSnapshot,
  type VoiceSessionTeardown,
} from "@/types/voice-chat-rtc"
import isEqual from "lodash/isEqual"

import { nowMillis } from "@/lib/date-utils"
import { errorMessage } from "@/lib/exception-utils"
import { ObjectSubscriptionManager } from "@/lib/object-subscription-manager"
import {
  CAPTION_GATE_GRACE_SECONDS,
  CAPTION_GATE_HIGH_LEVEL,
  CAPTION_GATE_LOW_LEVEL,
  CAPTION_LEAD_SECONDS,
  CAPTION_UNIT_GRAIN,
  CaptionSpeechGate,
  scanCaptionText,
} from "@/lib/voice-chat/caption-pacing"
import {
  CaptionRateMeter,
  type MeteredAssistantItem,
} from "@/lib/voice-chat/caption-rate-meter"
import { ResponseGate } from "@/lib/voice-chat/response-gate"
import { ToolCallRunner } from "@/lib/voice-chat/tool-call-runner"
import { ToolResultRelay } from "@/lib/voice-chat/tool-result-relay"
import { TranscriptPersistQueue } from "@/lib/voice-chat/transcript-persist-queue"
import {
  finalizedTranscriptTurnForRealtimeEvent,
  isFatalRealtimeError,
  liveItemVisibleText,
  parseVoicePlaceAnnotationTsv,
  parseVoiceRealtimeEvent,
  realtimeEventItemId,
  realtimeEventResponseId,
  toolArgumentsFromRealtimeEvent,
  toolArgumentsLabel,
  toolLocationLabel,
  toTurnTransitions,
  VOICE_PLACE_ANNOTATION_TSV_HEADER,
  VOICE_PLACE_ANNOTATION_TSV_MAX_LENGTH,
  voiceClockNow,
  voiceLiveItemEntries,
  voicePlaceAnnotationClientEvent,
  voiceRealtimeDebugEvent,
  voiceRealtimeDebugState,
  type VoiceTurnTransition,
} from "@/lib/voice-chat/voice-chat-utils"
import { toolCallProgressLabel } from "@/lib/voice-chat/voice-ui-utils"
import {
  currentTraceEventSeq,
  recordVoiceTrace,
  setVoiceTraceEventSeq,
  updateVoiceTraceContext,
  voiceTracer,
} from "@/lib/voice-trace/voice-trace"

// Owns the nonserializable Realtime/WebRTC session while middleware owns which
// instance is current and Redux projects its visible state. Provider item ids,
// response ids, tool-call ids, and the provider call id authorized through the
// backend identify different lifecycles; a persisted message id appears only
// after durability.
//
// ResponseGate serializes manual provider responses, ToolCallRunner owns tool
// completion delivery, and TranscriptPersistQueue owns FIFO transcript writes.
// This class authorizes normal persistence once a complete transcript has
// started playing; interruption, no-audio responses, and teardown settle the
// turns whose final heard prefix is not yet known.
const transportTrace = voiceTracer("transport")
const pipelineTrace = voiceTracer("pipeline")
const persistTrace = voiceTracer("persist")
const toolsTrace = voiceTracer("tools")
const captionsTrace = voiceTracer("captions")
const SKILL_TOOLS_ACKNOWLEDGEMENT_TIMEOUT_MS = 10_000
const SHORT_BARGE_IN_MAX_MS = 300
const INITIAL_RESPONSE_MIC_HOLD_TIMEOUT_MS = 45_000

interface VoiceSkillSnapshot {
  tools: VoiceRealtimeFunctionTool[]
  callIds: string[]
  toolProgressLabels?: Record<string, string>
  graphqlProgressLabels?: Record<string, string>
}

interface PendingSkillUpdate {
  snapshot: VoiceSkillSnapshot
  waiters: Array<(installed: boolean) => void>
}

interface InstallingSkillUpdate extends PendingSkillUpdate {
  eventId: string
  acknowledgementDeadline: number
}

type HistoryTransportMode = "collapsed" | "collapsed_chunked" | "per_item"

interface HistoryReplayState {
  maxMessageSize: number | null
  acknowledgement: {
    itemId: string
    timer: ReturnType<typeof setTimeout>
    startedAt: number
    bytes: number
    itemCount: number
    mode: HistoryTransportMode
  } | null
}

function isCollapsedHistoryItem(
  item: VoiceHistoryItem
): item is Extract<VoiceHistoryItem, { type: "message" }> {
  return (
    item.type === "message" &&
    item.role === "user" &&
    item.content[0]?.text.startsWith("BEGIN_PRIOR_SESSION_TRANSCRIPT_V1")
  )
}

function jsonBytes(value: unknown) {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength
}

function historyEvent(
  item: VoiceHistoryItem,
  index: number,
  startsTurn: boolean
) {
  return {
    type: "conversation.item.create",
    event_id: `voice_history_${index}`,
    previous_item_id: startsTurn ? "root" : `mindtrip_history_${index - 1}`,
    item: { ...item, id: `mindtrip_history_${index}` },
  } satisfies VoiceRealtimeClientEvent
}

function historyTransportSummary(
  collapsedItem: VoiceHistoryItem | undefined,
  eventCount: number,
  itemCount: number
): {
  collapsedBytes: number
  mode: HistoryTransportMode
  chunkCount: number
} {
  if (!collapsedItem) {
    return { collapsedBytes: 0, mode: "per_item", chunkCount: 0 }
  }
  if (eventCount === itemCount) {
    return {
      collapsedBytes: jsonBytes(collapsedItem),
      mode: "collapsed",
      chunkCount: 1,
    }
  }
  return {
    collapsedBytes: jsonBytes(collapsedItem),
    mode: "collapsed_chunked",
    chunkCount: eventCount - itemCount + 1,
  }
}

async function observeRejection(promise: Promise<unknown>) {
  try {
    await promise
  } catch {
    // The caller that owns the result handles the rejection.
  }
}
// Matches the orb's 25Hz getStats cadence (use-voice-audio-level.ts) —
// getStats is polled off the main audio path, so this never touches the
// WebRTC audio graph.
const SPEECH_GATE_SAMPLE_INTERVAL_MS = 40
// Normal sentence pauses regularly cross the 0.12s caption gate; only a much
// longer continuous low run represents the provider's multi-item dead air.
const ASSISTANT_AUDIO_STALL_SECONDS = 1.5
// Backend tool names the transport itself has to recognize: GraphQL calls are
// collected into one per-response batch request, and GraphQL batches plus
// undo/redo serialize through the edit chain so a later edit never overtakes
// an earlier one (D21/D23).
const GRAPHQL_QUERY_TOOL_NAME = "graphql_query"
const ITINERARY_UNDO_REDO_TOOL_NAME = "itinerary_undo_redo"
const CANCELLED_TOOL_CALL_RESULT: VoiceToolResult = {
  error:
    "This tool call was cancelled by an interruption before its arguments were fully sent. If the customer still wants this, call the tool again with complete arguments.",
  isError: true,
}
// The server truncates the interrupted item to the audio it believes played,
// but it cannot align transcript to audio precisely and the model then treats
// unheard text as said (chats 6881225 and 6881268 paged past stays that were
// never spoken). The caption's revealed text is the exact heard prefix, so the
// note states it and leaves what to do with it to the prompt's cut-off rules.
function cutOffNoteText(heardTranscript: string) {
  if (heardTranscript.trim().length === 0) {
    return "Context note: the customer interrupted the assistant's previous reply before hearing any of it."
  }
  return `Context note: the customer interrupted the assistant's previous reply. They heard only this part of it: ${JSON.stringify(heardTranscript)}. Nothing after that was heard.`
}
// Asking the model to "continue from where you left off" produced new wording
// every time (established on iOS, the reference pause implementation), which
// read as a gap. Handing it the remaining text verbatim means the words heard
// after resuming are the ones the turn was already going to say.
//
// `unfinished` is the pause that landed while the model was STILL GENERATING:
// the cancel that stops the turn also stops the writing, so the text held
// across the pause is all the answer will ever have (voice-trace chat 9211596
// paused 780ms in and stored a 61-character answer). Speaking only the
// remainder there ends the turn wherever the cancel happened to fall, so the
// verbatim contract covers the words that exist and the model closes out the
// ending it never got to write.
const pauseResumeDirective = (remainder: string, unfinished: boolean) =>
  `The user paused you mid-answer and has just resumed. ${
    unfinished
      ? "The pause cut you off before you finished writing this answer: say the following text word for word, then carry straight on and finish the answer in the same voice."
      : "Say the following text word for word, and nothing else."
  } Do not introduce it, do not summarize or rephrase it, do not greet the user, and do not mention the pause:\n${remainder}`
// Client-chosen id prefix for pause-resume directive items, so their provider
// echoes are recognizable wherever the transient item must be kept out of
// longer-lived state (see the place-annotation context exclusion).
const PAUSE_DIRECTIVE_ITEM_PREFIX = "mindtrip_pause_resume_"
// Union of carried and fresh place-annotation candidates for a merged turn.
// Fresh rows win their key (the newer extraction saw the surrounding text);
// carried rows the fresh extraction didn't re-find are prepended in their
// original order. Candidates carry no offsets — Rails expands each row to
// every bounded occurrence in the body — so a union is safe to resolve.
function mergePlaceAnnotationCandidates(
  carried: VoicePlaceAnnotationCandidate[],
  fresh: VoicePlaceAnnotationCandidate[]
): VoicePlaceAnnotationCandidate[] {
  if (carried.length === 0) return fresh
  // The SPAN is the identity: `name` and `type` are the model's description
  // of it, and the two extractions read different windows, so the same span
  // comes back worded differently ("Rome" then "Rome, Italy"). Keying on all
  // three let one span through twice, and Rails keys mentions on entity plus
  // offsets — two rows over one span resolve to two competing places rather
  // than collapsing.
  const key = (candidate: VoicePlaceAnnotationCandidate) =>
    candidate.originalText
  const freshKeys = new Set(fresh.map(key))
  return [
    ...carried.filter((candidate) => !freshKeys.has(key(candidate))),
    ...fresh,
  ]
}

// The nearest word boundary at or before `revealed`, so a resumed voice never
// starts mid-word. The cut lands *after* the boundary whitespace so the spoken
// half keeps it: a remainder that starts with a space comes back from the model
// without one (transcripts carry no leading whitespace), and the two halves
// would join as "a long railjourney". pauseResumeIndex backs up further from
// here, to the sentence the pause landed in.
function pauseCutIndex(text: string, revealed: number): number {
  const bounded = Math.min(revealed, text.length)
  if (bounded <= 0) return 0
  for (let index = bounded - 1; index > 0; index -= 1) {
    if (/\s/.test(text[index])) return index + 1
  }
  return bounded
}

// Already-spoken audio a resume may repeat to reach a natural boundary.
// Restarting mid-sentence hands the voice no run-up, so the continuation is
// read as a sentence of its own and the seam sounds like a different thought;
// restarting at the sentence the pause landed in also re-establishes what the
// listener was in the middle of. An unbounded rewind would replay far more
// than anyone asked for, so past this budget the cut settles for the nearest
// clause stop, and past that for the bare word boundary.
const PAUSE_RESUME_REPEAT_BUDGET_SECONDS = 4

// Where a resume starts speaking: the latest natural boundary before the word
// the audio stopped on, preferring a sentence start over a clause stop, and
// taking neither if reaching it would repeat more than `budgetMilli` of
// speech. Boundaries are scanned nearest-first, so the first candidate is the
// cheapest one — if it is over budget, every earlier one is worse.
function pauseResumeIndex(
  text: string,
  scan: ReturnType<typeof scanCaptionText>,
  anchor: number,
  budgetMilli: number
): number {
  const wordCut = pauseCutIndex(text, anchor)
  const anchorMilli = scan.cumulativeMilliunits[Math.min(anchor, text.length)]
  const nearestBoundary = (indices: number[]): number | null => {
    for (let index = indices.length - 1; index >= 0; index -= 1) {
      if (indices[index] >= wordCut) continue
      // Past the mark and its trailing space: the remainder must open on a
      // word, and the heard half keeps the boundary whitespace.
      let cut = indices[index] + 1
      while (cut < text.length && /\s/.test(text[cut])) cut += 1
      if (cut >= wordCut) return null
      if (anchorMilli - scan.cumulativeMilliunits[cut] > budgetMilli)
        return null
      return cut
    }
    return null
  }
  return (
    nearestBoundary(scan.sentenceEndIndices) ??
    nearestBoundary(scan.stopIndices) ??
    wordCut
  )
}
const TRANSCRIPT_DELTA_TRACE_CHECKPOINT_MS = 250
const TOOL_OUTPUT_TRACE_PREFIX_LENGTH = 200
const PLACE_ANNOTATION_TIMEOUT_MS = 10_000
const PLACE_ANNOTATION_FAILURE_RAW_TEXT_MAX_CODE_UNITS = 8 * 1024

function placeAnnotationFailureRawText(rawText: string): {
  text: string
  truncated: boolean
} {
  return {
    text: rawText.slice(0, PLACE_ANNOTATION_FAILURE_RAW_TEXT_MAX_CODE_UNITS),
    truncated:
      rawText.length > PLACE_ANNOTATION_FAILURE_RAW_TEXT_MAX_CODE_UNITS,
  }
}

interface EventPipelineCapture {
  suppressed: boolean
  userItemId: string | null
  latestUserItemId: string | null
  fullTranscriptBeforeTruncation: string | null
  // Snapshotted before the `audioStopped` transition snaps captions fully
  // revealed: how far the pacer actually got when the audio ended.
  audibleItemsAtBoundary: MeteredAssistantItem[] | null
}

interface StagedAssistantTurn {
  turn: VoiceFinalizedTranscriptTurn
  responseId: string
}

type PlaceAnnotationStatus = "staged" | "active" | "final"

interface PlaceAnnotationState {
  providerEventId: string
  sourceResponseId: string
  openaiCallId: string
  transcript: string
  input: VoiceRealtimeResponseInputItem[]
  requestEventId: string
  transcriptDoneAt: number
  responseId: string | null
  validationRetryCount: number
  toolCallIds?: string[]
  status: PlaceAnnotationStatus
  itemComplete: boolean
  rawText: string
  candidates: VoicePlaceAnnotationCandidate[]
  deliveryTail: Promise<void>
  transcriptSave: Promise<VoiceTranscriptPersistenceResult>
  timeoutId: ReturnType<typeof setTimeout> | null
}

type TranscriptDeltaTraceEvent =
  | Extract<
      VoiceRealtimeEvent,
      { type: "response.output_audio_transcript.delta" }
    >
  | Extract<
      VoiceRealtimeEvent,
      { type: "conversation.item.input_audio_transcription.delta" }
    >

interface TranscriptDeltaTraceCheckpoint {
  type: TranscriptDeltaTraceEvent["type"]
  itemId: string | null
  responseId: string | null
  hasEmitted: boolean
  deltaCount: number
  deltaChars: number
  eventSeqStart: number
  eventSeqEnd: number
  lastEmittedAt: number
  latestTextLen: number
  latestRevealedLen: number
  state: ReturnType<typeof voiceRealtimeDebugState>
}

interface GraphqlBatchEntry {
  callId: string
  outputIndex: number
  arguments: Record<string, unknown>
  userItemId: string
}

// One deferred per batch, keyed by call id: the promise has to exist before
// `response.done` can settle it, and every sibling call waits on the same
// request.
interface GraphqlBatch {
  entries: GraphqlBatchEntry[]
  resolve: (results: Map<string, VoiceToolResult>) => void
  reject: (error: unknown) => void
  results: Promise<Map<string, VoiceToolResult>>
}

interface ResponseToolOwnership {
  callIds: string[]
  ownerItemId: string | null
}

function realtimePayloadLength(raw: unknown) {
  if (typeof raw === "string") return raw.length
  if (raw instanceof ArrayBuffer) return raw.byteLength
  if (raw instanceof Blob) return raw.size
  return null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function mediaTrackDetail(track: MediaStreamTrack) {
  return {
    kind: track.kind,
    enabled: track.enabled,
    muted: track.muted,
    readyState: track.readyState,
  }
}

function hasSinkId(element: HTMLAudioElement): element is HTMLAudioElement & {
  setSinkId: (sinkId: string) => Promise<void>
} {
  return "setSinkId" in element
}

function audioElementWithSinkId(element: HTMLAudioElement | null) {
  if (!element || !hasSinkId(element)) return null
  return element
}

export class VoiceMicrophoneAcquisitionError extends Error {
  constructor(readonly originalError: unknown) {
    super("Voice microphone acquisition failed.")
    this.name = "VoiceMicrophoneAcquisitionError"
  }
}

export class VoiceMicrophoneUnsupportedError extends Error {
  constructor() {
    super("This browser does not support microphone access.")
    this.name = "VoiceMicrophoneUnsupportedError"
  }
}

export async function acquireMicrophone(
  constraints: MediaTrackConstraints
): Promise<MediaStream> {
  transportTrace.info("get_user_media_started")
  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new VoiceMicrophoneUnsupportedError()
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: constraints,
    })
    const audioTracks = stream.getAudioTracks()
    const audioTrack = audioTracks[0]
    let audioTrackDetail = {
      appliedEchoCancellation: null as boolean | string | null,
      appliedNoiseSuppression: null as boolean | null,
      appliedAutoGainControl: null as boolean | null,
      appliedVoiceIsolation: null as boolean | null,
      appliedChannelCount: null as number | null,
      inputDeviceLabel: null as string | null,
    }
    if (audioTrack) {
      // voiceIsolation is in the Media Capture extensions, not the installed DOM lib.
      const settings = audioTrack.getSettings() as MediaTrackSettings & {
        voiceIsolation?: boolean
      }
      audioTrackDetail = {
        appliedEchoCancellation: settings.echoCancellation ?? null,
        appliedNoiseSuppression: settings.noiseSuppression ?? null,
        appliedAutoGainControl: settings.autoGainControl ?? null,
        appliedVoiceIsolation: settings.voiceIsolation ?? null,
        appliedChannelCount: settings.channelCount ?? null,
        inputDeviceLabel: audioTrack.label,
      }
    }
    transportTrace.info("get_user_media_succeeded", {
      trackCount: stream.getTracks().length,
      audioTrackCount: audioTracks.length,
      trackKinds: stream.getTracks().map((track) => track.kind),
      ...audioTrackDetail,
    })
    return stream
  } catch (error) {
    transportTrace.warn("get_user_media_failed", {
      message: error instanceof Error ? error.message : String(error),
    })
    throw error
  }
}

// Thrown by `connect()` when the session is torn down mid-connect. The start
// listener treats it as a superseded connect (not a real start failure), so it
// tears down the dead instance without touching whatever session replaced it.
export class VoiceSessionConnectAbortedError extends Error {
  constructor() {
    super("Voice session connect was aborted by teardown.")
    this.name = "VoiceSessionConnectAbortedError"
  }
}

export class VoiceSessionConnectionError extends Error {
  constructor(readonly reason: string) {
    super("The voice connection ended unexpectedly.")
    this.name = "VoiceSessionConnectionError"
  }
}

/**
 * The live session orchestrator. It processes provider events synchronously in
 * arrival order, routes visible transitions to Redux, and retains runtime
 * resources outside serializable state.
 */
export class VoiceChatRTC {
  private peerConnection: RTCPeerConnection | null = null
  private dataChannel: RTCDataChannel | null = null
  private mediaStream: MediaStream | null = null
  private audioElement: HTMLAudioElement | null = null
  private startupStage:
    | "idle"
    | "creating_offer"
    | "negotiating"
    | "applying_answer"
    | "opening_data_channel"
    | "ready" = "idle"
  private microphoneState: "pending" | "installed" | "failed" = "pending"
  private negotiationState: "pending" | "succeeded" | "failed" = "pending"

  get startupDiagnostics() {
    return {
      startup_stage: this.startupStage,
      microphone_state: this.microphoneState,
      negotiation_state: this.negotiationState,
      peer_connection_state: this.peerConnection?.connectionState ?? null,
      ice_connection_state: this.peerConnection?.iceConnectionState ?? null,
      ice_gathering_state: this.peerConnection?.iceGatheringState ?? null,
      data_channel_state: this.dataChannel?.readyState ?? null,
    }
  }

  // Realtime can finish a response without ever creating an audio part. Callers
  // that need to distinguish that from audio whose playback has not started
  // yet can use this after response.done has been processed.
  responseFinishedWithoutAudio(responseId: string) {
    return this.responseAudioStates.get(responseId) === "no_audio"
  }

  private readonly chatId: number
  private openaiCallId: string | null = null
  private pendingOutputSpeed: number | null = null
  private lastSentOutputSpeed = 1.0
  private cutReason: VoiceCutoffReason | null = null

  private readonly persistQueue: TranscriptPersistQueue
  private readonly toolRunner: ToolCallRunner
  private readonly toolResultRelay: ToolResultRelay
  private readonly gate: ResponseGate
  private installedSkillSnapshot: VoiceSkillSnapshot | null = null
  private installingSkillUpdate: InstallingSkillUpdate | null = null
  private queuedSkillUpdate: PendingSkillUpdate | null = null
  private skillUpdateSequence = 0
  private readonly captionMeter: CaptionRateMeter
  // Speech gate over the assistant's playback level (getStats, sampled only
  // while an audio window is open). One instance feeds BOTH the pacer clock
  // (via onAssistantSpeakingChanged → slice) and the rate meter's speaking
  // time, so the two can never count time under different regimes.
  private readonly speechGate = new CaptionSpeechGate()
  private speechGateTimer: number | null = null
  private speechGateGeneration = 0
  private speechGateSampleInFlight = false
  private speechGateLastSampleAt = 0
  private lastEmittedSpeaking: boolean | null | undefined = undefined
  private lastEmittedWindowGated: boolean | undefined = undefined
  private lastEmittedAssistantAudioStalled: boolean | undefined = undefined
  private assistantAudioStalled = false
  // The open audio window's bookkeeping: which response id opened it (item
  // capture at stop spans from its first item), whether the gate was available
  // when it opened (the regime latch), and measured speaking time so far.
  private audibleWindowStartResponseId: string | null = null
  private audibleWindowGated = false
  private audibleWindowStartedAt = 0
  private audibleWindowSpeakingSeconds = 0
  // Per-window level distribution, traced at window end so gate thresholds
  // can be tuned from field traces: voice-trace-7369181 saw ONE detected
  // silence across 45s of speech, and without a level histogram there's no
  // way to tell whether pauses are shorter than MIN_SILENCE or just never
  // dip below LOW.
  private gateLevelStats = {
    samples: 0,
    nullSamples: 0,
    minLevel: Number.POSITIVE_INFINITY,
    maxLevel: 0,
    belowLow: 0,
    inBand: 0,
    aboveHigh: 0,
    longestLowRunSeconds: 0,
    currentLowRunSeconds: 0,
    lowRuns: 0,
  }
  // Transcript completion, audio completion, truncation, and teardown arrive on
  // separate timelines. Keep assistant turns staged until audible completion,
  // interruption, no-audio fallback, or session teardown resolves their path.
  private readonly responseUserItemIds: Record<string, string> = {}
  private readonly responseToolOwnership = new Map<
    string,
    ResponseToolOwnership
  >()
  // Every response that has produced output for a live assistant item, in
  // arrival order, keyed by the item (merged identity — continuation items
  // are aliased before this is recorded). The item's own `responseId` tracks
  // the audio it is CURRENTLY revealing against, which a pause-resume
  // continuation takes over; tool-call attribution needs the whole chain.
  private readonly assistantItemResponseIds = new Map<string, string[]>()
  private readonly placeAnnotationStates = new Map<
    string,
    PlaceAnnotationState
  >()
  private readonly placeAnnotationContextItemIds: string[] = []
  private readonly suppressedPlaceAnnotationResponseIds = new Set<string>()
  private readonly suppressedPlaceAnnotationItemIds = new Set<string>()
  private placeAnnotationRequestCounter = 0
  private readonly stagedAssistantTurns = new Map<string, StagedAssistantTurn>()
  private readonly terminalAssistantItemIds = new Set<string>()
  private readonly responseAudioStates = new Map<
    string,
    "generated" | "started" | "stopped" | "no_audio"
  >()
  private audioBufferActive = false
  // Set when a playback window opens, cleared when its duration is reported.
  private assistantAudioWindowStartedAtMs: number | null = null
  // When the buffer currently draining began playing. Unlike the window start
  // above, this restarts at every item boundary, so an interruption reports
  // how much of the item being spoken the customer actually heard rather than
  // how long a filler-plus-narration window had been running.
  private assistantAudioStartedAt: number | null = null
  private readonly clearedAssistantItemIds = new Set<string>()
  private readonly interruptedAssistantItemIds = new Set<string>()
  private readonly settledAssistantItemIds = new Set<string>()
  // User speech that can leave a cancelled assistant response stranded if its
  // transcript is empty. Audible interruptions also carry their provider audio
  // start so short barge-ins can be counted when speech stops.
  private readonly interruptingUserItems = new Map<
    string,
    { assistantAudioWasAudible: boolean; audioStartMs: number }
  >()
  private eventSeq = 0
  private lastPostDigest: string | null = null
  // Fired once per session, on data-channel open, to make the assistant speak
  // first. `greetingResponseId` is the response the greeting produced. It is
  // voice-only unless the caller opts into durable initial responses.
  private greetingSent = false
  private greetingResponseId: string | null = null
  // The greeting exists only to break the silence when voice mode opens, so a
  // customer who talks first makes it redundant. Set when the customer speaks so
  // sendGreeting() skips a greeting it hasn't sent yet; once the greeting is
  // already playing, Realtime's `interrupt_response` cancels it server side.
  private greetingSuperseded = false
  private micDisabledForInitialResponse = false
  private initialResponseMicTimer: ReturnType<typeof setTimeout> | null = null
  // A bounded flow can permanently disable its mic once it accepts a terminal
  // tool call. Incoming audio keeps playing, but incidental room noise can no
  // longer interrupt a closing response after the conversation is already over.
  private micDisabledForClosingResponse = false
  // The start sound plays out of the speakers the instant the call is up, and
  // echo cancellation has not converged on the first thing the page ever plays.
  // Held closed for its duration so the sound inviting the customer to speak
  // cannot itself be committed as what they said.
  private micDisabledForStartSound = false
  private holdCaptureUntil: Promise<void> | null = null
  private userMuted = false
  // A pause silences the ASSISTANT, not the session: the uplink keeps
  // following `userMuted` alone, because "stop talking so I can say
  // something" is the reason most pauses happen. Customer speech therefore
  // ends the pause rather than queueing behind it
  // (abandonPauseForUserSpeech). `pausedRemainder` is the pending speech the
  // pause cut off, re-spoken verbatim if the resume button gets there first
  // (see setPaused).
  private paused = false
  private pausedRemainder: string | null = null
  // The current pause-resume cycle: which live item the continuation merges
  // into, the merged text at the cut (prefixed onto the continuation's
  // terminal transcript so the finalized turn is the whole answer), and the
  // continuation response once `response.created` names it.
  private pauseContinuation: {
    itemId: string
    heardText: string
    responseId: string | null
    // Whether the pause's cancel stopped the answer being WRITTEN as well as
    // spoken, so the resume has to finish it (see pauseResumeDirective).
    unfinished: boolean
  } | null = null
  // The verbatim directive item backing the current resume. It is a one-shot
  // instruction — retracted once its continuation ends, because otherwise every
  // pause in a session leaves another copy of an answer's remainder sitting in
  // the context of every later turn.
  private pauseDirectiveItemId: string | null = null
  private pauseDirectiveCount = 0
  // Session-lifetime alias table: a continuation response's real provider item
  // ids map onto the paused item they merged into, so every later event for
  // them (deltas, terminal transcript, truncation, acknowledgements) lands on
  // the one merged turn. Never pruned mid-session — ids are unique and a
  // stale entry can only match its own item.
  private readonly continuationItemAliases = new Map<string, string>()
  // The heard prefix each continuation response was asked to continue from,
  // keyed by response id. The terminal transcript's prefix rewrite must
  // survive its continuation being superseded: a RE-pause replaces
  // `pauseContinuation` while the previous continuation's terminal transcript
  // is still in flight, and reading the prefix off the live continuation
  // would rewrite the merged turn down to that continuation's own portion.
  // Never pruned mid-session, for the same reason as the alias table.
  private readonly continuationHeardText = new Map<string, string>()
  // Every provider item id merged into a resumed turn, keyed by the merged
  // item. Presence marks a turn whose persisted message already exists (the
  // pause's interrupted write), so its finalized persist must replace rather
  // than dedupe, carrying these ids as provider_event_ids provenance.
  private readonly mergedTurnProviderIds = new Map<string, string[]>()
  // Gapless successor items whose unspoken text a pause folded into the
  // paused row. Their own rows were blanked, so their late terminal
  // transitions (a cancelled response's transcript done) must not refill
  // them — see transitionSuppressedForPause.
  private readonly foldedSuccessorItemIds = new Set<string>()
  // Place-annotation candidates carried across a pause, keyed by item. Every
  // finalized annotation banks its (unioned) candidates here — the annotation
  // state self-deletes once its delivery completes, so a later pause's attach
  // may find no state to carry from. A pre-pause extraction covers (at most)
  // the text up to its cut and its resolution is trimmed to the interrupted
  // body; the continuation's fresh extraction sees only the remainder item.
  // Neither alone covers the merged answer — the fresh delivery unions the
  // banked candidates back in so the replace-all resolution annotates the
  // whole body (candidates are offset-free; Rails expands them by string
  // match).
  private readonly mergedAnnotationCandidates = new Map<
    string,
    VoicePlaceAnnotationCandidate[]
  >()
  // The one truncation the pause's own clear will produce, identified by RAW
  // provider item id: after aliasing, a later barge-in truncation of the
  // continuation carries the same merged id, and only the pause's own event
  // may keep the dimmed remainder on screen. `handlingPauseTruncation` marks
  // it for the synchronous duration of its pipeline pass.
  private pendingPauseTruncationItemId: string | null = null
  private handlingPauseTruncation = false
  // Resolves once the realtime data channel is open and client events can be
  // sent; lets sendGreeting() defer without the caller reasoning about it.
  private dataChannelOpen: Promise<void> | null = null
  private resolveDataChannelOpen: (() => void) | null = null
  private rejectDataChannelOpen: ((error: Error) => void) | null = null
  private rejectStartup: ((error: Error) => void) | null = null
  private transportReady = false
  private connectionLossNotified = false
  private disconnectedTimer: ReturnType<typeof setTimeout> | null = null
  private historyItems: VoiceHistoryItem[] = []
  private historyReplay: HistoryReplayState = {
    maxMessageSize: null,
    acknowledgement: null,
  }
  // The session's localized tool progress copy from the create response, held
  // here with the rest of the session machinery so the label can ride the
  // tool-started event — the entry must render localized on its first frame.
  private toolProgressLabels: Record<string, string> = {}
  private contextOnlyToolNames: string[] = []
  private readonly transcriptDeltaCheckpoints = new Map<
    string,
    TranscriptDeltaTraceCheckpoint
  >()
  // Sibling `graphql_query` calls of one provider response, collected until
  // that response is done and then sent as one Rails batch.
  private readonly pendingGraphqlBatches = new Map<string, GraphqlBatch>()
  // Serializes trip edits leaving this session: an edit dispatches only after
  // the previous edit's request settled, so a read issued after an edit sees
  // the edited trip and the Rails per-chat lock stays a backstop (D21/D23).
  private editChain: Promise<unknown> = Promise.resolve()
  private closed = false

  private voiceChatEventHandler: ((data: VoiceChatEventData) => void) | null =
    null
  private removeSocketConnectedCallback: (() => void) | null = null

  constructor(private deps: VoiceChatRTCDeps) {
    this.chatId = deps.chatId
    this.micDisabledForInitialResponse = deps.muteDuringInitialResponse ?? false
    this.captionMeter = new CaptionRateMeter({
      onCaptionRateMeasured: deps.onCaptionRateMeasured,
      snapshot: () => {
        const state = deps.getVoiceState()
        return {
          selectedSpeed: state.selectedSpeed,
        }
      },
    })
    this.persistQueue = new TranscriptPersistQueue({
      persistTranscript: deps.persistTranscript,
      onTurnPersisting: deps.onTurnPersisting,
      onTurnPersisted: (turn, result) => {
        deps.onTurnPersisted({
          providerEventId: turn.providerEventId,
          interrupted: turn.interrupted === true,
          ...result,
        })
        if (turn.role === "assistant" && result.deleted !== true) {
          this.captionMeter.turnPersisted(
            turn.providerEventId,
            result.detectedLanguage
          )
        }
      },
      onTurnPersistFailed: deps.onTurnPersistFailed,
    })
    this.gate = new ResponseGate({
      send: (event) => this.send(event),
      isAudioBufferActive: () => this.audioBufferActive,
      // The words the pause held back are never going to be spoken, so the
      // turn settles exactly as a customer-ended pause does.
      onPauseResumeUndeliverable: () => this.dropHeldRemainder(),
    })
    this.toolResultRelay = new ToolResultRelay({
      fetchToolMedia: ({ openaiCallId, callId }) =>
        deps.fetchToolMedia({
          chatId: this.chatId,
          openaiCallId,
          callId,
        }),
      send: (event) => this.send(event),
      isContextOnlyTool: (toolName) =>
        this.contextOnlyToolNames.includes(toolName),
      hasOtherToolWorkOutstanding: (callId) =>
        this.gate.hasUndeliveredToolResults() ||
        this.deps
          .getVoiceState()
          .liveStream.some(
            (entry) =>
              entry.kind === "tool" &&
              entry.toolCall.callId !== callId &&
              entry.toolCall.status === "pending" &&
              !this.contextOnlyToolNames.includes(entry.toolCall.toolName)
          ),
      trace: toolsTrace,
      gate: this.gate,
    })
    this.toolRunner = new ToolCallRunner({
      fetchToolResult: deps.fetchToolResult,
      onToolProgress: deps.onToolProgress,
      onToolSucceeded: deps.onToolSucceeded,
      onToolFailed: deps.onToolFailed,
      applyResponseConfiguration: (configuration) =>
        this.installSkillSnapshot(configuration),
      sendToolOutput: (delivery) =>
        this.toolResultRelay.deliver(delivery, this.openaiCallId),
      refreshToolCalls: deps.refreshToolCalls,
    })
  }

  async connect(opts: {
    mediaStream: Promise<MediaStream>
    negotiate: (sdpOffer: string) => Promise<{
      sdpAnswer: string
      openaiCallId: string
      historyItems: VoiceHistoryItem[]
      contextOnlyToolNames?: string[]
      toolProgressLabels?: Record<string, string>
    }>
  }): Promise<{ openaiCallId: string }> {
    this.closed = false
    this.transportReady = false
    this.connectionLossNotified = false
    this.clearDisconnectedTimer()
    // Closing a peer connection can leave its pending SDP promises unsettled.
    // Every startup wait must also observe microphone, transport, and close errors.
    const startupFailed = new Promise<never>((_resolve, reject) => {
      this.rejectStartup = reject
    })
    void observeRejection(startupFailed)

    try {
      this.startupStage = "creating_offer"
      this.microphoneState = "pending"
      this.negotiationState = "pending"
      const peerConnection = new RTCPeerConnection()
      const audioSender = peerConnection.addTransceiver("audio", {
        direction: "sendrecv",
      }).sender
      const dataChannel = peerConnection.createDataChannel("oai-events")

      this.peerConnection = peerConnection
      this.dataChannel = dataChannel

      // Installation starts now so a mic that is still prompting overlaps the
      // offer and negotiation; failures also interrupt any pending SDP operation.
      const installingMediaStream = this.installMediaStream(
        opts.mediaStream,
        audioSender,
        peerConnection
      )
      void observeRejection(installingMediaStream)

      peerConnection.onconnectionstatechange = () => {
        transportTrace.info("peer_connection_state_changed", {
          connectionState: peerConnection.connectionState,
        })
        switch (peerConnection.connectionState) {
          case "new":
          case "connecting":
            break
          case "connected":
            this.clearDisconnectedTimer()
            this.flushQueuedSkillUpdate()
            if (this.transportReady) {
              this.deps.onTransportReconnectingChanged?.(false)
            }
            break
          case "disconnected":
            if (this.transportReady) {
              this.deps.onTransportReconnectingChanged?.(true)
              this.scheduleDisconnectedFailure()
            }
            break
          case "failed":
            if (this.transportReady) {
              this.deps.onTransportReconnectingChanged?.(true)
            }
            this.failConnection("peer_connection_failed")
            break
          case "closed":
            this.failConnection(
              `peer_connection_${peerConnection.connectionState}`
            )
            break
        }
      }
      // Mirrors the connectionState arms above: browsers do not report the two
      // states in lockstep, and both the scheduler and its cancel are
      // idempotent, so a browser that reports both is unaffected.
      peerConnection.oniceconnectionstatechange = () => {
        transportTrace.info("peer_ice_connection_state_changed", {
          iceConnectionState: peerConnection.iceConnectionState,
        })
        switch (peerConnection.iceConnectionState) {
          case "failed":
            if (this.transportReady) {
              this.deps.onTransportReconnectingChanged?.(true)
            }
            this.failConnection("ice_connection_failed")
            break
          case "disconnected":
            if (this.transportReady) {
              this.deps.onTransportReconnectingChanged?.(true)
              this.scheduleDisconnectedFailure()
            }
            break
          case "connected":
          case "completed":
            this.clearDisconnectedTimer()
            if (this.transportReady) {
              this.deps.onTransportReconnectingChanged?.(false)
            }
            break
          default:
            break
        }
      }
      peerConnection.onicegatheringstatechange = () => {
        transportTrace.info("peer_ice_gathering_state_changed", {
          iceGatheringState: peerConnection.iceGatheringState,
        })
      }

      this.dataChannelOpen = new Promise<void>((resolve, reject) => {
        this.resolveDataChannelOpen = resolve
        this.rejectDataChannelOpen = reject
        dataChannel.onopen = () => {
          transportTrace.info("data_channel_opened")
          this.markTransportReady()
        }
      })
      // close() may reject this while negotiation is still pending. Attach a
      // handler immediately so intentional cancellation never becomes an
      // unhandled rejection; connect() still observes the original promise.
      void observeRejection(this.dataChannelOpen)
      dataChannel.onclose = () => {
        transportTrace.info("data_channel_closed")
        this.failConnection("data_channel_closed")
      }
      dataChannel.onerror = () => {
        transportTrace.warn("data_channel_error")
        this.failConnection("data_channel_error")
      }
      dataChannel.onmessage = (message) => {
        this.handleMessage(message.data)
      }

      peerConnection.ontrack = (event) => {
        transportTrace.info("remote_audio_track_received", {
          streamCount: event.streams.length,
          trackKind: event.track.kind,
        })
        this.handleRemoteTrack(event.streams[0])
      }

      const offer = await Promise.race([
        startupFailed,
        peerConnection.createOffer(),
      ])
      await Promise.race([
        startupFailed,
        peerConnection.setLocalDescription(offer),
      ])
      transportTrace.info("sdp_offer_created")

      if (!offer.sdp) {
        throw new Error("Browser did not create an SDP offer.")
      }
      if (this.closed) throw new VoiceSessionConnectAbortedError()

      // OpenAI closes calls whose ICE never completes, and the stored call id
      // is harmless because the next session overwrites it.
      this.startupStage = "negotiating"
      const [session] = await Promise.race([
        startupFailed,
        Promise.all([
          opts.negotiate(offer.sdp).then(
            (session) => {
              this.negotiationState = "succeeded"
              return session
            },
            (error: unknown) => {
              this.negotiationState = "failed"
              throw error
            }
          ),
          installingMediaStream,
        ]),
      ])
      if (this.closed) {
        peerConnection.close()
        throw new VoiceSessionConnectAbortedError()
      }
      this.startupStage = "applying_answer"
      await Promise.race([
        startupFailed,
        peerConnection.setRemoteDescription({
          type: "answer",
          sdp: session.sdpAnswer,
        }),
      ])
      transportTrace.info("remote_description_applied")
      if (this.closed) throw new VoiceSessionConnectAbortedError()

      const maxMessageSize = peerConnection.sctp?.maxMessageSize ?? 0
      if (maxMessageSize > 0) {
        this.historyReplay.maxMessageSize = maxMessageSize
      } else {
        transportTrace.warn("history_transport_limit_unavailable")
        this.historyReplay.maxMessageSize = null
      }

      this.openaiCallId = session.openaiCallId
      this.historyItems = session.historyItems
      this.contextOnlyToolNames = session.contextOnlyToolNames ?? []
      this.toolProgressLabels = session.toolProgressLabels ?? {}
      this.pendingOutputSpeed = null
      this.lastSentOutputSpeed = this.deps.getVoiceState().selectedSpeed
      updateVoiceTraceContext({ openaiCallId: session.openaiCallId })

      // Some browser/test implementations can already be open by the time the
      // remote description settles without delivering another open event.
      this.startupStage = "opening_data_channel"
      if (dataChannel.readyState === "open") this.markTransportReady()
      await Promise.race([startupFailed, this.dataChannelOpen])
      if (this.closed) throw new VoiceSessionConnectAbortedError()

      this.startupStage = "ready"
      if (this.micDisabledForInitialResponse) {
        // A missing/rejected greeting lifecycle must not leave a connected
        // session permanently unable to hear the customer.
        this.initialResponseMicTimer = setTimeout(() => {
          this.releaseInitialResponseMic("timeout")
        }, INITIAL_RESPONSE_MIC_HOLD_TIMEOUT_MS)
      }
      return { openaiCallId: session.openaiCallId }
    } catch (error) {
      transportTrace.warn("voice_session_connect_failed", {
        message: errorMessage(error, "Could not start voice session."),
      })
      throw error
    } finally {
      this.rejectStartup = null
    }
  }

  private async installMediaStream(
    mediaStream: Promise<MediaStream>,
    audioSender: RTCRtpSender,
    peerConnection: RTCPeerConnection
  ) {
    try {
      const stream = await mediaStream
      this.mediaStream = stream
      if (this.closed) {
        stream.getTracks().forEach((track) => track.stop())
        throw new VoiceSessionConnectAbortedError()
      }

      this.applyMicEnabled()
      const track = stream.getAudioTracks()[0]
      stream.getTracks().forEach((mediaTrack) => {
        mediaTrack.onended = () => {
          transportTrace.warn("media_track_ended", mediaTrackDetail(mediaTrack))
        }
        mediaTrack.onmute = () => {
          transportTrace.warn("media_track_muted", mediaTrackDetail(mediaTrack))
        }
        mediaTrack.onunmute = () => {
          transportTrace.info(
            "media_track_unmuted",
            mediaTrackDetail(mediaTrack)
          )
        }
      })
      await audioSender.replaceTrack(track)
      this.microphoneState = "installed"
    } catch (error) {
      this.microphoneState = "failed"
      const startupError =
        error instanceof VoiceSessionConnectAbortedError
          ? error
          : new VoiceMicrophoneAcquisitionError(error)
      // Preserve the microphone cause before close can trigger transport errors.
      this.rejectPendingConnect(startupError)
      peerConnection.close()
      throw startupError
    }
  }

  send(event: VoiceRealtimeClientEvent) {
    if (event.type === "response.create") {
      const instructions = this.deps.getResponseInstructions?.(event.response)
      if (instructions) {
        event = { ...event, response: { ...event.response, instructions } }
      }
    }
    transportTrace.info(event.type, this.clientEventTraceDetail(event), "out")
    if (!this.dataChannel) {
      transportTrace.warn("client_event_send_skipped", {
        eventType: event.type,
        reason: "missing_data_channel",
      })
      return false
    }
    if (this.dataChannel.readyState !== "open") {
      transportTrace.warn("client_event_send_skipped", {
        eventType: event.type,
        reason: "data_channel_not_open",
        readyState: this.dataChannel.readyState,
      })
      return false
    }

    try {
      this.dataChannel.send(JSON.stringify(event))

      return true
    } catch (error) {
      transportTrace.warn("client_event_send_failed", {
        eventType: event.type,
        message: errorMessage(error, "Could not send client event."),
      })
      return false
    }
  }

  private installSkillSnapshot(
    configuration: VoiceSkillResponseConfiguration
  ): Promise<boolean> {
    const snapshot = {
      tools: configuration.tools,
      callIds: configuration.callIds,
      toolProgressLabels: configuration.toolProgressLabels,
      graphqlProgressLabels: configuration.graphqlProgressLabels,
    }

    if (this.snapshotCovers(this.installedSkillSnapshot, snapshot)) {
      const installedSnapshot = this.installedSkillSnapshot
      if (installedSnapshot) {
        this.installedSkillSnapshot = {
          ...installedSnapshot,
          toolProgressLabels:
            snapshot.toolProgressLabels === undefined
              ? installedSnapshot.toolProgressLabels
              : {
                  ...installedSnapshot.toolProgressLabels,
                  ...snapshot.toolProgressLabels,
                },
          graphqlProgressLabels:
            snapshot.graphqlProgressLabels === undefined
              ? installedSnapshot.graphqlProgressLabels
              : {
                  ...installedSnapshot.graphqlProgressLabels,
                  ...snapshot.graphqlProgressLabels,
                },
        }
      }
      return Promise.resolve(true)
    }

    return new Promise<boolean>((resolve) => {
      const pending = { snapshot, waiters: [resolve] }
      if (this.joinOrQueueSkillUpdate(pending)) {
        this.flushQueuedSkillUpdate()
        return
      }

      resolve(false)
      this.failSkillInstallation("skill_tools_snapshot_incomparable")
    })
  }

  private joinOrQueueSkillUpdate(update: PendingSkillUpdate) {
    const queued = this.queuedSkillUpdate
    if (queued) {
      if (this.snapshotCovers(queued.snapshot, update.snapshot)) {
        queued.waiters.push(...update.waiters)
        return true
      }
      if (this.snapshotCovers(update.snapshot, queued.snapshot)) {
        update.waiters.push(...queued.waiters)
        this.queuedSkillUpdate = update
        return true
      }
      return false
    }

    const installing = this.installingSkillUpdate
    if (installing) {
      if (this.snapshotCovers(installing.snapshot, update.snapshot)) {
        installing.waiters.push(...update.waiters)
        return true
      }
      if (this.snapshotCovers(update.snapshot, installing.snapshot)) {
        this.queuedSkillUpdate = update
        return true
      }
      return false
    }

    this.queuedSkillUpdate = update
    return true
  }

  private snapshotCovers(
    covering: VoiceSkillSnapshot | null,
    covered: VoiceSkillSnapshot
  ) {
    return (
      covering !== null &&
      covered.callIds.every((callId) => covering.callIds.includes(callId))
    )
  }

  private flushQueuedSkillUpdate() {
    if (this.closed || this.installingSkillUpdate || !this.queuedSkillUpdate) {
      return
    }

    const eventId = `voice_skill_tools_${++this.skillUpdateSequence}`
    const sent = this.send({
      type: "session.update",
      event_id: eventId,
      session: {
        type: "realtime",
        tools: this.queuedSkillUpdate.snapshot.tools,
      },
    })
    if (!sent) return

    const update = this.queuedSkillUpdate
    this.queuedSkillUpdate = null
    this.installingSkillUpdate = {
      ...update,
      eventId,
      acknowledgementDeadline: window.setTimeout(() => {
        if (this.installingSkillUpdate?.eventId !== eventId) return
        this.failSkillInstallation("skill_tools_acknowledgement_timeout")
      }, SKILL_TOOLS_ACKNOWLEDGEMENT_TIMEOUT_MS),
    }
  }

  private acknowledgeSkillUpdate(tools: VoiceRealtimeFunctionTool[]) {
    const installing = this.installingSkillUpdate
    if (!installing || !isEqual(tools, installing.snapshot.tools)) return

    window.clearTimeout(installing.acknowledgementDeadline)
    this.installingSkillUpdate = null
    this.installedSkillSnapshot = installing.snapshot
    installing.waiters.forEach((resolve) => resolve(true))
    this.flushQueuedSkillUpdate()
  }

  private rejectSkillUpdate(eventId: string | undefined) {
    if (!eventId || eventId !== this.installingSkillUpdate?.eventId) return
    this.failSkillInstallation("skill_tools_update_rejected")
  }

  private failSkillInstallation(reason: string) {
    const installing = this.installingSkillUpdate
    if (installing) {
      window.clearTimeout(installing.acknowledgementDeadline)
      installing.waiters.forEach((resolve) => resolve(false))
      this.installingSkillUpdate = null
    }
    this.queuedSkillUpdate?.waiters.forEach((resolve) => resolve(false))
    this.queuedSkillUpdate = null
    this.failConnection(reason)
  }

  private clearSkillSnapshots() {
    const installing = this.installingSkillUpdate
    if (installing) {
      window.clearTimeout(installing.acknowledgementDeadline)
      installing.waiters.forEach((resolve) => resolve(false))
    }
    this.queuedSkillUpdate?.waiters.forEach((resolve) => resolve(false))
    this.installedSkillSnapshot = null
    this.installingSkillUpdate = null
    this.queuedSkillUpdate = null
  }

  setMuted(muted: boolean) {
    this.userMuted = muted
    this.applyMicEnabled()
  }

  // Lands a host's pause/mute flags on the engine in the one order that is
  // safe, so neither host (voice mode's middleware, the interview adapter)
  // carries it: mic before the hold on pause — pausing with the uplink
  // still open leaves a hot-mic gap whose speech_started would abandon the
  // pause it belongs to — and the continuation before the mic on resume, so
  // it claims the response lock ahead of the reopened uplink. setPaused is
  // idempotent, so a plain mute change passes through as mic only.
  applyControls({ paused, muted }: { paused: boolean; muted: boolean }) {
    if (paused) {
      this.setMuted(muted)
      this.setPaused(true)
    } else {
      this.setPaused(false)
      this.setMuted(muted)
    }
  }

  // The window between end_conversation starting and its result landing,
  // where the goodbye is already protected but the host's ending state is
  // not yet known. A host reads it rather than keeping a copy.
  get micHeldForClosingResponse() {
    return this.micDisabledForClosingResponse
  }

  // Stops or restarts the assistant's half of the session: its speech is cut
  // server-side (see stopAssistantForPause) and resuming re-speaks the cut-off
  // remainder verbatim so the answer continues rather than being
  // re-improvised. The mic deliberately keeps following setMuted alone — the
  // pause's auto-mute is store policy (mutedByPause), driven through the same
  // setMuted, so an unmute mid-pause reopens the uplink without touching the
  // held answer. Speech that gets through ends the pause
  // (abandonPauseForUserSpeech).
  setPaused(paused: boolean) {
    if (this.paused === paused) return
    this.paused = paused
    // The sink mutes immediately: output_audio_buffer.clear stops the server's
    // send, but only after a round trip — the tap must feel instant. Restored
    // here on resume and in close(), so a session ended while paused can't
    // leave the app-wide sink muted for its successor.
    if (this.audioElement) {
      this.audioElement.muted = paused
    }
    if (paused) {
      // Gate first: the clear's own audioCleared callback must not flush a
      // queued tool narration into the pause.
      this.gate.setPaused(true)
      this.stopAssistantForPause()
    } else {
      // Continuation before lifting the gate's hold, so the remainder's
      // response.create owns the response lock ahead of any tool narration
      // that queued up while paused.
      if (this.requestContinuationAfterPause()) {
        this.gate.setPaused(false)
      } else {
        transportTrace.info("pause_resume_skipped_stale_narration", {
          itemId: null,
          heardLength: 0,
        })
        this.gate.resumeWithoutContinuation()
      }
    }
  }

  // The customer talking IS the answer to "should the assistant carry on?".
  // The pause auto-mutes the uplink, but unmuting mid-pause reopens it exactly
  // so they can (and a speech_started already in flight when the pause's mute
  // lands arrives here too), so their
  // speech ends the pause instead of queueing behind it: the held remainder is
  // dropped, the sink reopens for whatever they asked for, and the paused
  // row's dimmed tail is trimmed away — those words are never going to be
  // spoken now, and the pause's interrupted write already persisted only the
  // heard prefix. Pressing resume afterwards is not a thing the UI offers: the
  // transition clears the store's paused flag with the same dispatch.
  private abandonPauseForUserSpeech() {
    if (!this.paused) return
    this.paused = false
    if (this.audioElement) {
      this.audioElement.muted = false
    }
    // Not gate.setPaused(false): lifting the hold flushes whatever queued up
    // while paused, and a tool narration starting over a customer mid-sentence
    // is exactly what the gate's speech handling exists to prevent. Dropping
    // the flag leaves that queue to the live turn now underway.
    this.gate.abandonPause()
    const continuation = this.dropHeldRemainder()
    transportTrace.info("pause_abandoned_for_speech", {
      itemId: continuation?.itemId ?? null,
      heardLength: continuation?.heardText.length ?? 0,
    })
  }

  // Releases everything the pause was holding for a resume that is no longer
  // coming, and trims the row's dimmed tail back to the heard prefix — those
  // words will never be spoken now, and the pause's interrupted write already
  // persisted exactly that prefix. The pause's own truncation becomes a plain
  // barge-in trim, so its id disarms too.
  private dropHeldRemainder() {
    const continuation = this.pauseContinuation
    this.pausedRemainder = null
    this.pauseContinuation = null
    this.pendingPauseTruncationItemId = null
    this.deletePauseDirectiveItem()
    this.deps.onTurnTransition({
      kind: "pauseAbandoned",
      id: continuation?.itemId ?? null,
      heardLength: continuation?.heardText.length ?? 0,
    })
    return continuation
  }

  // Retracts the resume directive once it has been acted on. Sent as its own
  // client event rather than trimmed from the conversation locally: the item
  // lives in the provider's copy, which is the one that grows.
  private deletePauseDirectiveItem() {
    const itemId = this.pauseDirectiveItemId
    if (!itemId) return
    this.pauseDirectiveItemId = null
    this.send({ type: "conversation.item.delete", item_id: itemId })
  }

  // Stops the assistant mid-turn and records how far the user got, so resuming
  // can continue from there. Two separate things may need stopping, and a
  // pause mid-speech usually only involves the second:
  //
  // - Generation, if the model is still producing the turn. It generates
  //   faster than real time, so `response.done` normally arrives long before
  //   the audio finishes playing — gating the whole pause on it would make a
  //   pause mid-speech cancel nothing.
  // - Playout, which the server drives in real time over the audio track.
  //   `output_audio_buffer.clear` is what stops the send; the server answers
  //   it with `conversation.item.truncated`, whose interrupted persistence of
  //   the heard prefix rides the same path a barge-in does (cutoffReason
  //   "user_stopped"). Its CAPTION trim is suppressed for the paused item
  //   (transitionSuppressedForPause): the dimmed unspoken tail stays on
  //   screen through the pause and the resume reveals over it.
  private stopAssistantForPause() {
    const state = this.deps.getVoiceState()
    const activeResponseId = state.activeAssistantResponseId
    const wasPlaying = this.audioBufferActive
    if (activeResponseId === null && !wasPlaying) return

    // Taken before the cancel lands, so the split sees a transcript that has
    // stopped growing (turnPaused's `interrupted` closes it against deltas
    // still in flight).
    const pauseCut = this.takePauseCut(state)
    let pauseTruncationItemId: string | null = null
    this.pausedRemainder = pauseCut?.remainder ?? null
    this.cutReason = "user_stopped"

    if (pauseCut) {
      // Gapless successors queued behind the audible item never played. Their
      // text folds into the paused row (the turnPaused text below), so their
      // own rows blank now — otherwise the same words would sit dimmed in two
      // places, and the resume's `responseStarted` snap would pop them fully
      // revealed. Their later terminal transitions are suppressed for the
      // same reason (transitionSuppressedForPause).
      pauseCut.successorItemIds.forEach((itemId) => {
        this.foldedSuccessorItemIds.add(itemId)
        this.interruptedAssistantItemIds.add(itemId)
        this.settleAssistantItem(itemId)
        this.deps.onTurnTransition({
          kind: "turnInterrupted",
          id: itemId,
          audioEndMs: 0,
        })
      })

      // Something audible was cut mid-turn: set the continuation up to merge
      // back into that item. Nothing heard (heardText empty) reads fine as a
      // fresh turn, and merging there would resurrect a message the blank
      // interrupted write is about to delete.
      if (pauseCut.heardText.length > 0) {
        this.pauseContinuation = {
          itemId: pauseCut.itemId,
          heardText: pauseCut.heardText,
          responseId: null,
          // Cancelling generation stops the answer being WRITTEN, not just
          // spoken, so this turn's text now ends wherever the cancel fell.
          // Generation outruns playback, so a pause while the response is
          // still active is the common case early in a turn — and the earlier
          // the pause, the more of the answer never exists.
          unfinished: activeResponseId !== null,
        }
        // The clear below truncates the item whose audio is PLAYING, and its
        // event carries the item's RAW provider id. For a first pause that is
        // the merged item itself; for a RE-paused merged turn it is the
        // latest continuation's real item (the alias rewrites it before
        // anything else sees it, but transitionSuppressedForPause keys on
        // the raw id) — arming the merged id here made the second pause's
        // truncation read as a barge-in and trim the kept dimmed tail
        // (voice-trace chat 9211593). mergedTurnProviderIds appends real
        // items in merge order, so its tail is the playing item's raw id —
        // read before the successors below join it.
        pauseTruncationItemId =
          this.mergedTurnProviderIds.get(pauseCut.itemId)?.at(-1) ??
          pauseCut.itemId
        if (!this.mergedTurnProviderIds.has(pauseCut.itemId)) {
          this.mergedTurnProviderIds.set(pauseCut.itemId, [pauseCut.itemId])
        }
        const mergedIds = this.mergedTurnProviderIds.get(pauseCut.itemId)
        pauseCut.successorItemIds.forEach((itemId) => mergedIds?.push(itemId))
        // Freeze the reveal at the word boundary the resume will continue
        // from, keep (and absorb the successors') unspoken text as the dimmed
        // tail, and close the item against in-flight deltas.
        this.deps.onTurnTransition({
          kind: "turnPaused",
          id: pauseCut.itemId,
          heardLength: pauseCut.heardText.length,
          text: pauseCut.heardText + pauseCut.remainder,
        })
      }
    }

    if (activeResponseId !== null) {
      this.send({ type: "response.cancel", response_id: activeResponseId })
    }
    // Armed only alongside the clear that produces the truncation: a pause
    // taken with the playout buffer already drained sends no clear, so no
    // truncation arrives to consume the id, and a stale one sat waiting to
    // swallow the next genuine barge-in trim on that item.
    this.pendingPauseTruncationItemId = wasPlaying
      ? pauseTruncationItemId
      : null
    if (wasPlaying) {
      this.send({ type: "output_audio_buffer.clear" })
    }
    transportTrace.info("assistant_stopped_for_pause", {
      remainderLength: this.pausedRemainder?.length ?? 0,
      cancelledGeneration: activeResponseId !== null,
      clearedPlayout: wasPlaying,
      continuationItemId: pauseCut?.heardText.length ? pauseCut.itemId : null,
    })
  }

  // Splits the session's pending speech at the point the voice reached,
  // returning the text that was never spoken. Fully played turns snap fully
  // revealed (`audioStopped`), so unrevealed assistant text is exactly the
  // speech a pause cuts off — including gapless successor responses queued
  // behind the audible one, whose ids come back so the pause can retire them.
  private takePauseCut(state: VoiceSessionStateSnapshot): {
    itemId: string
    responseId: string | null
    heardText: string
    remainder: string
    successorItemIds: string[]
  } | null {
    const items = voiceLiveItemEntries(state)
      .map((entry) => entry.item)
      .filter((item) => item.role === "assistant" && !item.interrupted)
    const firstUnrevealed = items.findIndex(
      (item) => item.revealedLength < item.text.length
    )
    if (firstUnrevealed === -1) return null

    const pending = items.slice(firstUnrevealed)
    const full = pending.map((item) => item.text).join("")
    const revealed = pending.reduce(
      (length, item) => length + item.revealedLength,
      0
    )
    // The reveal deliberately runs CAPTION_LEAD_SECONDS ahead of the voice
    // (the eye needs the word before the ear confirms it), so cutting at the
    // reveal position made the resume SKIP the led words — the seam dropped
    // a beat of speech. Back the anchor up by the lead's worth of speech
    // units first; the boundary snap then errs toward a slight repeat, which
    // reads as resuming, where a skip reads as a glitch. From there
    // pauseResumeIndex backs up again to the sentence (or clause) the voice
    // was in the middle of, within a bounded repeat budget.
    const scan = scanCaptionText(full)
    const leadMilli = Math.round(
      CAPTION_LEAD_SECONDS *
        state.captionSyllablesPerSecond *
        CAPTION_UNIT_GRAIN
    )
    const anchorMilli = Math.max(
      0,
      scan.cumulativeMilliunits[Math.min(revealed, full.length)] - leadMilli
    )
    let anchor = Math.min(revealed, full.length)
    while (anchor > 0 && scan.cumulativeMilliunits[anchor] > anchorMilli) {
      anchor -= 1
    }
    // Reveal is sequential, so the cut lands inside the first pending item.
    const cut = pauseResumeIndex(
      full,
      scan,
      anchor,
      Math.round(
        PAUSE_RESUME_REPEAT_BUDGET_SECONDS *
          state.captionSyllablesPerSecond *
          CAPTION_UNIT_GRAIN
      )
    )
    if (cut >= full.length) return null
    return {
      itemId: pending[0].itemId,
      responseId: pending[0].responseId ?? null,
      heardText: full.slice(0, cut),
      remainder: full.slice(cut),
      successorItemIds: pending.slice(1).map((item) => item.itemId),
    }
  }

  // The words the model wrote between the cut and the cancel landing. The cut
  // is taken while deltas are still in flight and the paused item is closed
  // against the ones that follow, so the terminal transcript of the cancelled
  // response is the only place that tail ever appears — and the pause's own
  // audio clear has already marked that response stale, so the projection
  // never sees it (voice-trace chat 9211594 held 310 characters of a
  // 319-character transcript, and spoke and stored 310). Fold it into both the
  // held remainder and the dimmed tail. A prefix that no longer matches means
  // the cut absorbed gapless successors whose text this transcript knows
  // nothing about: leave that cut alone.
  private extendPauseCutFromTerminalTranscript(
    event: Extract<
      VoiceRealtimeEvent,
      { type: "response.output_audio_transcript.done" }
    >
  ) {
    const continuation = this.pauseContinuation
    if (!this.paused || !continuation || continuation.responseId !== null)
      return
    if (event.item_id !== continuation.itemId) return

    const heldText = continuation.heardText + (this.pausedRemainder ?? "")
    if (event.transcript.length <= heldText.length) return
    if (!event.transcript.startsWith(heldText)) return

    this.pausedRemainder = event.transcript.slice(continuation.heardText.length)
    this.deps.onTurnTransition({
      kind: "turnPaused",
      id: continuation.itemId,
      heardLength: continuation.heardText.length,
      text: event.transcript,
    })
    pipelineTrace.info("pause_cut_extended", {
      itemId: continuation.itemId,
      heardLength: continuation.heardText.length,
      heldLength: heldText.length,
      transcriptLength: event.transcript.length,
    })
  }

  // Speaks the exact text the pause cut off, so the answer resumes rather than
  // being re-improvised. Sent as a conversation item rather than
  // `response.instructions`, which replaces the session's own instructions for
  // that response and would drop the assistant's persona and tools for the one
  // turn that most needs to sound like the turn it is finishing.
  private requestContinuationAfterPause() {
    let remainder = this.pausedRemainder
    this.pausedRemainder = null
    // Prefer the live text over the capture: a cancelled response's terminal
    // transcript can land during the pause with a slightly fuller text than
    // the deltas had delivered by the cut, and the dimmed tail on screen —
    // which the continuation must match — is that live text.
    const continuation = this.pauseContinuation
    if (continuation) {
      const pausedItem = voiceLiveItemEntries(this.deps.getVoiceState()).find(
        (entry) => entry.item.itemId === continuation.itemId
      )?.item
      if (pausedItem) {
        const liveRemainder = pausedItem.text.slice(
          continuation.heardText.length
        )
        remainder = liveRemainder.length > 0 ? liveRemainder : remainder
      }
    }
    if (remainder === null) {
      this.pauseContinuation = null
      return false
    }

    // A re-pause installs a fresh directive while the previous continuation is
    // still running, so its directive never reaches the response.done retract.
    this.deletePauseDirectiveItem()
    this.pauseDirectiveCount += 1
    const directiveItemId = `${PAUSE_DIRECTIVE_ITEM_PREFIX}${this.pauseDirectiveCount}`
    this.send({
      type: "conversation.item.create",
      item: {
        type: "message",
        id: directiveItemId,
        role: "system",
        content: [
          {
            type: "input_text",
            text: pauseResumeDirective(
              remainder,
              continuation?.unfinished === true
            ),
          },
        ],
      },
    })
    this.pauseDirectiveItemId = directiveItemId
    this.gate.requestPauseResume()
    return true
  }

  // The pause_resume response has been named: reopen the paused turn so the
  // continuation's items merge into it (see aliasPauseContinuationEvent). The
  // pause's terminal bookkeeping is unwound because this item now has a live
  // continuation — its finalized transcript persists again, replacing the
  // interrupted heard-prefix write.
  private attachPauseContinuation(responseId: string) {
    const continuation = this.pauseContinuation
    if (!continuation || continuation.responseId !== null) return
    continuation.responseId = responseId
    this.continuationHeardText.set(responseId, continuation.heardText)

    const itemId = continuation.itemId
    this.interruptedAssistantItemIds.delete(itemId)
    this.settledAssistantItemIds.delete(itemId)
    this.clearedAssistantItemIds.delete(itemId)
    this.terminalAssistantItemIds.delete(itemId)
    // Retire any still-live pre-pause annotation and carry its candidates:
    // the merged transcript.done must stage a FRESH extraction (the duplicate
    // guard would otherwise skip it and the resumed tail would never
    // annotate), and its delivery unions these back in so the prefix keeps
    // its places. A completed annotation has already banked its candidates at
    // finalize — its state self-deleted after delivery.
    const staleAnnotation = this.placeAnnotationStates.get(itemId)
    if (staleAnnotation) {
      this.mergedAnnotationCandidates.set(
        itemId,
        mergePlaceAnnotationCandidates(
          this.mergedAnnotationCandidates.get(itemId) ?? [],
          staleAnnotation.candidates
        )
      )
      this.cleanupPlaceAnnotation(staleAnnotation)
    }
    // A paused greeting stays a greeting: retag so the merged turn keeps the
    // greeting's voice-only persistence rules.
    if (this.isGreetingTurn(itemId, this.deps.getVoiceState())) {
      this.greetingResponseId = responseId
    }
    // heardLength becomes the item's pausePrefixLength: the continuation
    // window's pacing and metering both exclude the prefix its audio never
    // speaks.
    this.deps.onTurnTransition({
      kind: "turnResumed",
      id: itemId,
      heardLength: continuation.heardText.length,
    })
    pipelineTrace.info("pause_continuation_attached", {
      responseId,
      itemId,
      heardLength: continuation.heardText.length,
    })
  }

  // A continuation response's real item ids alias onto the paused item, so
  // the resumed answer continues within one turn — one caption row, one
  // persisted message — instead of opening a new item mid-sentence.
  private learnContinuationItem(responseId: string, realItemId: string) {
    const continuation = this.pauseContinuation
    if (continuation?.responseId !== responseId) return
    if (realItemId === continuation.itemId) return
    if (this.continuationItemAliases.has(realItemId)) return

    this.continuationItemAliases.set(realItemId, continuation.itemId)
    this.mergedTurnProviderIds.get(continuation.itemId)?.push(realItemId)
    // The merged turn's fresh annotation must reference the continuation's
    // REAL item — that is where the remainder lives server-side (the merged
    // id resolves to the truncated heard prefix). Context ids are otherwise
    // collected post-alias, so the real id would never enter the window.
    if (!this.placeAnnotationContextItemIds.includes(realItemId)) {
      this.placeAnnotationContextItemIds.push(realItemId)
    }
    pipelineTrace.info("pause_continuation_item_merged", {
      responseId,
      realItemId,
      mergedItemId: continuation.itemId,
    })
  }

  // Rewrites a continuation item's identity onto the merged turn before the
  // pipeline sees the event, so transitions, staging, persistence, and
  // truncation all address one item. The terminal transcript additionally
  // gains the heard prefix: the provider only knows the remainder it was
  // asked to speak, but the finalized turn is the whole answer.
  private aliasPauseContinuationEvent(
    event: VoiceRealtimeEvent
  ): VoiceRealtimeEvent {
    switch (event.type) {
      case "response.output_item.added":
      case "response.output_item.done": {
        if (event.item.type === "message") {
          this.learnContinuationItem(event.response_id, event.item.id)
        }
        const mergedId = this.continuationItemAliases.get(event.item.id)
        if (!mergedId) return event
        return { ...event, item: { ...event.item, id: mergedId } }
      }
      case "response.content_part.added": {
        this.learnContinuationItem(event.response_id, event.item_id)
        const mergedId = this.continuationItemAliases.get(event.item_id)
        if (!mergedId) return event
        return { ...event, item_id: mergedId }
      }
      case "response.output_audio_transcript.delta": {
        this.learnContinuationItem(event.response_id, event.item_id)
        const mergedId = this.continuationItemAliases.get(event.item_id)
        if (!mergedId) return event
        // The merged row already holds the remainder as its dimmed tail
        // (turnPaused kept it), and the continuation speaks that same text —
        // appending its deltas would show the words twice. The emptied delta
        // still flips the row's response identity so the pacer reveals it
        // against the continuation's audio; the terminal transcript then
        // reconciles the text to what was actually said.
        return { ...event, item_id: mergedId, delta: "" }
      }
      case "response.output_audio_transcript.done": {
        this.learnContinuationItem(event.response_id, event.item_id)
        const mergedId = this.continuationItemAliases.get(event.item_id)
        if (!mergedId) return event
        // Keyed by RESPONSE, not by the live continuation: a re-pause swaps
        // `pauseContinuation` out while this transcript is still in flight,
        // and an unprefixed rewrite would cut the merged turn down to this
        // continuation's own portion.
        const heardText = this.continuationHeardText.get(event.response_id)
        const transcript =
          heardText === undefined
            ? event.transcript
            : heardText + event.transcript
        return { ...event, item_id: mergedId, transcript }
      }
      case "conversation.item.created":
      case "conversation.item.added":
      case "conversation.item.done":
      case "conversation.item.retrieved": {
        const mergedId = this.continuationItemAliases.get(event.item.id)
        if (!mergedId) return event
        return { ...event, item: { ...event.item, id: mergedId } }
      }
      case "conversation.item.truncated": {
        const mergedId = this.continuationItemAliases.get(event.item_id)
        if (!mergedId) return event
        return { ...event, item_id: mergedId }
      }
      case "response.done": {
        // The terminal response echoes its output items under their REAL
        // ids; the annotation source selection (and diagnostics) must see
        // the merged identity like every other consumer.
        const response = event.response
        const output = response?.output
        if (!response || !output) return event
        if (!output.some((item) => this.continuationItemAliases.has(item.id))) {
          return event
        }
        return {
          ...event,
          response: {
            ...response,
            output: output.map((item) => {
              const mergedId = this.continuationItemAliases.get(item.id)
              return mergedId ? { ...item, id: mergedId } : item
            }),
          },
        }
      }
      default:
        return event
    }
  }

  // Projection-only suppression for the pause: the engine's own bookkeeping
  // for these events (interrupted persistence, settlement) still runs in
  // afterTransitions — what must not run is the Redux write that would erase
  // text the caption is deliberately keeping on screen.
  private transitionSuppressedForPause(transition: VoiceTurnTransition) {
    // The pause's own truncation must not trim the paused row: turnPaused
    // already froze the reveal at the cut and kept the unspoken tail dimmed.
    // A barge-in truncation of the resumed continuation is a different raw
    // item and trims normally (handlingPauseTruncation is raw-id keyed).
    if (transition.kind === "turnInterrupted") {
      return this.handlingPauseTruncation
    }
    // A folded successor's cancelled response can still deliver its terminal
    // transcript; letting turnCompleted refill the blanked row would put the
    // folded text on screen twice.
    if (transition.kind === "turnCompleted") {
      return this.foldedSuccessorItemIds.has(transition.id)
    }
    return false
  }

  setStartSoundPlaying(playing: boolean) {
    // The release can land after a fast hang-up; a closed instance has no
    // tracks to reopen and must not trace into whichever session is live now.
    if (this.closed || this.micDisabledForStartSound === playing) return
    this.micDisabledForStartSound = playing
    this.applyMicEnabled()
    pipelineTrace.info("mic_gated_for_start_sound", { gated: playing })
  }

  disableMicForClosingResponse() {
    if (this.micDisabledForClosingResponse) return
    this.micDisabledForClosingResponse = true
    this.applyMicEnabled()
    pipelineTrace.info("mic_disabled_for_closing_response")
  }

  restoreMicAfterClosingError() {
    if (this.closed || !this.micDisabledForClosingResponse) return
    this.micDisabledForClosingResponse = false
    this.applyMicEnabled()
    pipelineTrace.info("mic_restored_after_closing_error")
  }

  private releaseInitialResponseMic(
    reason:
      | "response_finished"
      | "greeting_error"
      | "greeting_skipped"
      | "timeout",
    responseId?: string
  ) {
    if (
      this.closed ||
      !this.micDisabledForInitialResponse ||
      (reason === "response_finished" && responseId !== this.greetingResponseId)
    ) {
      return
    }
    this.micDisabledForInitialResponse = false
    this.clearInitialResponseMicTimer()
    this.applyMicEnabled()
    pipelineTrace.info("mic_restored_after_initial_response", {
      responseId,
      reason,
    })
  }

  private clearInitialResponseMicTimer() {
    if (this.initialResponseMicTimer === null) return
    clearTimeout(this.initialResponseMicTimer)
    this.initialResponseMicTimer = null
  }

  // The track's enabled state is always derived from every gate rather than set
  // directly, so releasing one gate cannot override another.
  private applyMicEnabled() {
    const enabled =
      !this.userMuted &&
      !this.micDisabledForInitialResponse &&
      !this.micDisabledForClosingResponse &&
      !this.micDisabledForStartSound
    this.mediaStream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled
    })
  }

  setAudioElement(element: HTMLAudioElement | null) {
    this.audioElement = element
  }

  // Sample mic/assistant playback levels for the voice orb via getStats.
  // Deliberately NOT a Web Audio AudioContext: `createMediaStreamSource`
  // disables Chrome's echo canceller, letting the assistant's playback leak
  // into the mic and re-trigger semantic VAD so the assistant interrupts
  // itself. Returns null for a channel the browser reports no level for; the
  // caller falls back to a synthetic pulse.
  async sampleAudioLevels(): Promise<{
    mic: number | null
    assistant: number | null
  }> {
    const peerConnection = this.peerConnection
    if (!peerConnection) return { mic: null, assistant: null }

    let report: RTCStatsReport
    try {
      report = await peerConnection.getStats()
    } catch {
      return { mic: null, assistant: null }
    }

    let mic: number | null = null
    let assistant: number | null = null
    // The DOM types stats as `any`; narrow from unknown. The mic's capture
    // level is on `media-source`, the assistant's playback on `inbound-rtp`.
    report.forEach((stat: unknown) => {
      if (!isRecord(stat)) return
      const type = typeof stat.type === "string" ? stat.type : ""
      const audioLevel =
        typeof stat.audioLevel === "number" ? stat.audioLevel : null
      if (type === "media-source") {
        if (audioLevel !== null) mic = audioLevel
        return
      }
      if (type !== "inbound-rtp") return
      if (typeof stat.kind === "string" && stat.kind !== "audio") return
      if (audioLevel !== null) assistant = audioLevel
    })
    return { mic, assistant }
  }

  // A new audio window opened: reset the window's speaking clock, LATCH
  // whether the gate is live for the window (rate measurement and reveal
  // clock must count time under the same regime for the window's whole
  // life), and start sampling. The loop runs only while assistant audio is
  // in the buffer.
  private startSpeechGateWindow(responseId: string) {
    this.stopSpeechGateLoop()
    this.speechGateGeneration += 1
    this.assistantAudioStalled = false
    this.audibleWindowStartResponseId = responseId
    this.audibleWindowSpeakingSeconds = 0
    this.gateLevelStats = {
      samples: 0,
      nullSamples: 0,
      minLevel: Number.POSITIVE_INFINITY,
      maxLevel: 0,
      belowLow: 0,
      inBand: 0,
      aboveHigh: 0,
      longestLowRunSeconds: 0,
      currentLowRunSeconds: 0,
      lowRuns: 0,
    }
    this.speechGate.windowStarted()
    this.audibleWindowGated = this.speechGate.available
    this.emitSpeakingState(null)
    this.audibleWindowStartedAt = voiceClockNow()
    this.speechGateLastSampleAt = this.audibleWindowStartedAt
    this.speechGateTimer = window.setInterval(
      () => void this.sampleSpeechGate(),
      SPEECH_GATE_SAMPLE_INTERVAL_MS
    )
  }

  private stopSpeechGateLoop() {
    if (this.speechGateTimer !== null) {
      window.clearInterval(this.speechGateTimer)
      this.speechGateTimer = null
      const stats = this.gateLevelStats
      if (stats.samples > 0) {
        captionsTrace.info("caption_speech_gate_stats", {
          windowGated: this.audibleWindowGated,
          speakingSeconds: this.audibleWindowSpeakingSeconds,
          samples: stats.samples,
          nullSamples: stats.nullSamples,
          minLevel: Number.isFinite(stats.minLevel) ? stats.minLevel : null,
          maxLevel: stats.maxLevel,
          belowLow: stats.belowLow,
          inBand: stats.inBand,
          aboveHigh: stats.aboveHigh,
          lowRuns: stats.lowRuns,
          longestLowRunSeconds: stats.longestLowRunSeconds,
        })
      }
    }
  }

  private resetAssistantAudioStalled() {
    if (!this.assistantAudioStalled) return
    this.assistantAudioStalled = false
    this.emitSpeakingState(null)
  }

  private recordGateLevelSample(level: number | null, dtSeconds: number) {
    const stats = this.gateLevelStats
    stats.samples += 1
    if (level === null) {
      stats.nullSamples += 1
      stats.currentLowRunSeconds = 0
      return
    }
    stats.minLevel = Math.min(stats.minLevel, level)
    stats.maxLevel = Math.max(stats.maxLevel, level)
    if (level <= CAPTION_GATE_LOW_LEVEL) {
      stats.belowLow += 1
      if (stats.currentLowRunSeconds === 0) stats.lowRuns += 1
      stats.currentLowRunSeconds += dtSeconds
      stats.longestLowRunSeconds = Math.max(
        stats.longestLowRunSeconds,
        stats.currentLowRunSeconds
      )
    } else {
      stats.currentLowRunSeconds = 0
      if (level >= CAPTION_GATE_HIGH_LEVEL) stats.aboveHigh += 1
      else stats.inBand += 1
    }
  }

  private async sampleSpeechGate() {
    // Skip if a prior getStats hasn't resolved yet — slow samples must not
    // pile up behind the interval; the next sample's dt covers the gap.
    if (this.speechGateSampleInFlight || this.speechGateTimer === null) return
    this.speechGateSampleInFlight = true
    const generation = this.speechGateGeneration
    try {
      const { assistant } = await this.sampleAudioLevels()
      // The window may have closed or been replaced while getStats resolved.
      if (
        this.speechGateTimer === null ||
        generation !== this.speechGateGeneration
      ) {
        return
      }
      const now = voiceClockNow()
      const dtSeconds = (now - this.speechGateLastSampleAt) / 1000
      this.speechGateLastSampleAt = now
      this.recordGateLevelSample(assistant, dtSeconds)
      const state = this.speechGate.sample({ level: assistant, dtSeconds })
      this.assistantAudioStalled =
        state === "silent" &&
        (this.assistantAudioStalled ||
          this.gateLevelStats.currentLowRunSeconds >=
            ASSISTANT_AUDIO_STALL_SECONDS)
      // A session's first window opens BEFORE the gate can have proven itself
      // (availability takes one HIGH sample), so the window-open latch always
      // starts that window ungated. The gate appearing within the grace
      // upgrades the window — the unavailable ticks already counted as
      // speaking, so the measured clock is identical under both regimes. The
      // upgrade reaches the scheduler through emitSpeakingState's broadcast;
      // this latch is the regime's single owner.
      if (
        !this.audibleWindowGated &&
        this.speechGate.available &&
        (now - this.audibleWindowStartedAt) / 1000 < CAPTION_GATE_GRACE_SECONDS
      ) {
        this.audibleWindowGated = true
      }
      // `unavailable` counts as speaking (fail open) — but those windows are
      // latched ungated, so the meter ignores this clock anyway.
      if (state !== "silent") {
        this.audibleWindowSpeakingSeconds += dtSeconds
      }
      this.emitSpeakingState(assistant)
    } finally {
      this.speechGateSampleInFlight = false
    }
  }

  private emitSpeakingState(level: number | null) {
    const state = this.speechGate.state
    const speaking = state === "unavailable" ? null : state === "speaking"
    const windowGated = this.audibleWindowGated
    // Dedup on the complete playback-level projection: a grace upgrade or a
    // sustained low run can change the UI without a speech-gate transition.
    if (
      speaking === this.lastEmittedSpeaking &&
      windowGated === this.lastEmittedWindowGated &&
      this.assistantAudioStalled === this.lastEmittedAssistantAudioStalled
    ) {
      return
    }
    this.lastEmittedSpeaking = speaking
    this.lastEmittedWindowGated = windowGated
    this.lastEmittedAssistantAudioStalled = this.assistantAudioStalled
    this.deps.onAssistantSpeakingChanged({
      speaking,
      windowGated,
      assistantAudioStalled: this.assistantAudioStalled,
    })
    captionsTrace.info("caption_speech_gate", {
      state,
      level,
      windowGated,
      assistantAudioStalled: this.assistantAudioStalled,
      speakingSeconds: this.audibleWindowSpeakingSeconds,
    })
  }

  // A physical boundary belongs to the open ordered window only when its
  // response appears at or after the response that opened the window. Closing
  // the identity here prevents synchronous downstream callbacks from accepting
  // a duplicate stop or clear for the same playback window.
  private audibleWindowItemsAtBoundary(
    state: VoiceSessionStateSnapshot,
    boundaryResponseId: string
  ): MeteredAssistantItem[] | null {
    const startResponseId = this.audibleWindowStartResponseId
    if (!startResponseId) return null

    const entries = voiceLiveItemEntries(state).filter(
      (entry) => entry.item.role === "assistant"
    )
    const startIndex = entries.findIndex(
      (entry) => entry.item.responseId === startResponseId
    )
    let endIndex = -1
    entries.forEach((entry, index) => {
      if (entry.item.responseId === boundaryResponseId) endIndex = index
    })
    if (startIndex === -1 || endIndex < startIndex) return null

    this.audibleWindowStartResponseId = null
    return entries.slice(startIndex, endIndex + 1).map((entry) => {
      // A merged turn's heard prefix played in the window BEFORE its pause;
      // metering this window against the full merged text would inflate the
      // measured caption rate, so the prefix is backed out here (same
      // exclusion the caption pacer's selectors apply).
      const meteringPrefix = entry.item.pausePrefixLength ?? 0
      return {
        itemId: entry.item.itemId,
        text: entry.item.text.slice(meteringPrefix),
        revealedLength: Math.max(0, entry.item.revealedLength - meteringPrefix),
      }
    })
  }

  async setOutputSink(deviceId: string, speakerSelectionSupported: boolean) {
    const audioElement = audioElementWithSinkId(this.audioElement)
    if (!speakerSelectionSupported || !audioElement) {
      transportTrace.info("audio_output_skipped", {
        speakerSelectionSupported,
        hasAudioElement: Boolean(audioElement),
      })
      return
    }

    const sinkId = deviceId === VOICE_AUDIO_SYSTEM_DEVICE_ID ? "" : deviceId

    try {
      await audioElement.setSinkId(sinkId)
      transportTrace.info("audio_output_succeeded", {
        outputDeviceMode:
          deviceId === VOICE_AUDIO_SYSTEM_DEVICE_ID ? "system" : "selected",
      })
    } catch (error) {
      transportTrace.warn("audio_output_failed", {
        message: errorMessage(error, "Could not set audio output."),
      })
    }
  }

  applySelectedOutputDevice(deviceId: string) {
    return this.setOutputSink(
      deviceId,
      this.deps.getVoiceState().speakerSelectionSupported
    )
  }

  close() {
    this.closed = true
    this.clearInitialResponseMicTimer()
    if (this.historyReplay.acknowledgement) {
      clearTimeout(this.historyReplay.acknowledgement.timer)
      this.historyReplay.acknowledgement = null
    }
    this.clearDisconnectedTimer()
    this.rejectPendingConnect(new VoiceSessionConnectAbortedError())
    this.unsubscribeVoiceChat()
    this.toolRunner.teardown()
    this.clearSkillSnapshots()
    this.toolResultRelay.close()
    this.pendingGraphqlBatches.forEach((batch) => {
      batch.reject(
        new Error("Voice session closed before the GraphQL batch ran.")
      )
    })
    this.pendingGraphqlBatches.clear()
    this.clearPlaceAnnotations()
    this.gate.clearTimers()
    this.stopSpeechGateLoop()
    this.resetAssistantAudioStalled()
    this.dataChannel?.close()
    // The capture can outlive the transport: a sound riding the live call
    // (call-sounds.ts) keeps the tracks until it settles.
    const senders = this.peerConnection?.getSenders() ?? []
    const stream = this.mediaStream
    const stopCapture = () => {
      senders.forEach((sender) => sender.track?.stop())
      stream?.getTracks().forEach((track) => track.stop())
    }
    if (this.holdCaptureUntil) {
      void this.holdCaptureUntil.finally(stopCapture)
    } else {
      stopCapture()
    }
    this.peerConnection?.close()

    this.dataChannel = null
    this.peerConnection = null
    this.mediaStream = null
    if (this.audioElement) {
      // The sink is app-wide and outlives this session: a session ended while
      // paused must not leave it muted for the next one.
      this.audioElement.muted = false
      this.audioElement.srcObject = null
    }
    updateVoiceTraceContext({ openaiCallId: null })
  }

  private markTransportReady() {
    if (this.transportReady || this.closed) return
    this.transportReady = true
    const resolve = this.resolveDataChannelOpen
    this.resolveDataChannelOpen = null
    this.rejectDataChannelOpen = null
    resolve?.()
    this.flushQueuedSkillUpdate()
  }

  private rejectPendingConnect(error: Error) {
    this.rejectStartup?.(error)
    if (this.transportReady) return
    const reject = this.rejectDataChannelOpen
    this.resolveDataChannelOpen = null
    this.rejectDataChannelOpen = null
    reject?.(error)
  }

  private failConnection(reason: string) {
    this.persistQueue.rejectOutstanding(reason)
    if (this.closed) return

    const error = new VoiceSessionConnectionError(reason)
    if (this.rejectStartup || !this.transportReady) {
      this.rejectPendingConnect(error)
      return
    }
    if (this.connectionLossNotified) return
    this.connectionLossNotified = true
    this.clearDisconnectedTimer()
    this.deps.onConnectionLost?.(error)
  }

  private scheduleDisconnectedFailure() {
    if (
      this.closed ||
      !this.deps.onConnectionLost ||
      this.disconnectedTimer !== null
    ) {
      return
    }
    this.disconnectedTimer = setTimeout(() => {
      this.disconnectedTimer = null
      this.failConnection("peer_connection_disconnected")
    }, 5_000)
  }

  private clearDisconnectedTimer() {
    if (this.disconnectedTimer === null) return
    clearTimeout(this.disconnectedTimer)
    this.disconnectedTimer = null
  }

  subscribeVoiceChat() {
    this.unsubscribeVoiceChat()
    const handleVoiceChatEvent = (data: VoiceChatEventData) => {
      if (data.type === "chat_renamed") {
        this.deps.onChatRenamed?.({ chatId: data.chat_id, name: data.name })
      } else {
        this.toolRunner.handleSocketEvent(data)
      }
    }
    this.voiceChatEventHandler = handleVoiceChatEvent
    toolsTrace.info("voice_chat_subscription_requested", {
      chatId: this.chatId,
    })
    void ObjectSubscriptionManager.instance.subscribeToVoiceChat(
      this.chatId,
      handleVoiceChatEvent
    )
    this.removeSocketConnectedCallback =
      ObjectSubscriptionManager.instance.addSocketConnectedCallback(() => {
        this.toolRunner.resync("reconnect")
      })
  }

  stopAssistant() {
    const currentVoiceState = this.deps.getVoiceState()
    transportTrace.info("assistant_stop_requested", {
      connectionStatus: currentVoiceState.connectionStatus,
      hasActiveAssistantResponseId: Boolean(
        currentVoiceState.activeAssistantResponseId
      ),
    })
    if (
      currentVoiceState.connectionStatus !== "assistant_speaking" ||
      !currentVoiceState.activeAssistantResponseId
    ) {
      return
    }

    this.cutReason = "user_stopped"

    this.send({
      type: "response.cancel",
      response_id: currentVoiceState.activeAssistantResponseId,
    })
    this.send({ type: "output_audio_buffer.clear" })
    transportTrace.info("assistant_interrupt_manual_stop", {
      responseId: currentVoiceState.activeAssistantResponseId,
      itemId: currentVoiceState.activeAssistantItemId,
    })
  }

  selectOutputSpeed(speed: number) {
    this.pendingOutputSpeed = speed
    this.flushOutputSpeed()
  }

  flushOutputSpeed() {
    const currentState = this.deps.getVoiceState()
    if (currentState.connectionStatus === "idle") {
      this.lastSentOutputSpeed =
        this.pendingOutputSpeed ?? currentState.selectedSpeed
      this.pendingOutputSpeed = null
      return
    }
    if (currentState.connectionStatus === "connecting") return
    if (currentState.selectedSpeed !== this.lastSentOutputSpeed) {
      this.pendingOutputSpeed = currentState.selectedSpeed
    }

    const speed = this.pendingOutputSpeed
    if (speed === null) return
    if (speed === this.lastSentOutputSpeed) {
      this.pendingOutputSpeed = null
      return
    }

    if (!this.outputSpeedSessionUpdateReady(currentState)) return

    const sent = this.send({
      type: "session.update",
      session: { type: "realtime", audio: { output: { speed } } },
    })
    if (!sent) return

    this.pendingOutputSpeed = null
    this.lastSentOutputSpeed = speed
    transportTrace.info("voice_output_speed_update_sent", { speed }, "out")
  }

  reconcileRunningToolCalls(
    chatId: number,
    openaiCallId: string,
    runningCalls: ReconcileRunningVoiceToolCall[],
    trigger: string
  ) {
    this.toolRunner.reconcile(
      chatId,
      openaiCallId,
      this.gate.currentCustomerSpeechGeneration,
      runningCalls,
      trigger
    )
  }

  /**
   * Flushes this session's final transcript state, captures the queue settlement
   * boundary, and schedules transport closure. Middleware drops current-instance
   * ownership synchronously; reconciliation remains tied to the captured stopped
   * chat/session rather than whichever RTC starts next.
   */
  teardown(holdCaptureUntil?: Promise<void>): VoiceSessionTeardown {
    const beforeStopState = this.deps.getVoiceState()
    transportTrace.info("voice_session_stop_requested", {
      connectionStatus: beforeStopState.connectionStatus,
      selectedChatId: beforeStopState.selectedChatId,
      hasOpenaiCallId: Boolean(this.openaiCallId),
    })
    this.flushUnpersistedAssistantTurns(beforeStopState)
    this.responseToolOwnership.clear()
    this.assistantItemResponseIds.clear()
    this.clearAssistantFinalizationState()
    this.persistQueue.rejectOutstanding("session_stop")
    const pendingPersists = this.persistQueue.settled
    const stoppedChatId = beforeStopState.selectedChatId
    const shouldReconcileStoppedSession = this.openaiCallId !== null

    this.reportAssistantAudioWindow()
    this.pendingOutputSpeed = null
    this.lastSentOutputSpeed = beforeStopState.selectedSpeed
    this.captionMeter.sessionStopped()
    this.holdCaptureUntil = holdCaptureUntil ?? null
    queueMicrotask(() => this.close())
    this.openaiCallId = null
    this.cutReason = null
    persistTrace.info("transcript_persist_queue_reset")

    return { stoppedChatId, shouldReconcileStoppedSession, pendingPersists }
  }

  // A window that is still open when the session ends still played; stopping
  // mid-reply must not drop its seconds from the session total.
  private reportAssistantAudioWindow() {
    const startedAtMs = this.assistantAudioWindowStartedAtMs
    if (startedAtMs === null) return
    this.assistantAudioWindowStartedAtMs = null
    this.deps.onAssistantAudioElapsed?.(nowMillis() - startedAtMs)
  }

  // The sink element carries no `autoplay` (it is mounted app-wide, on pages
  // with no session), so playback starts here — the first moment there is
  // anything to play. The opening connect rides the click that opened voice
  // mode; a rebuilt session attaches its track minutes later with no gesture
  // behind it, which is where an autoplay refusal is plausible.
  private handleRemoteTrack(stream: MediaStream) {
    const audioElement = this.audioElement
    if (!audioElement) return
    audioElement.srcObject = stream
    void this.startAudioPlayback(audioElement)
    void this.applySelectedOutputDevice(
      this.deps.getVoiceState().selectedOutputDeviceId
    )
  }

  private async startAudioPlayback(audioElement: HTMLAudioElement) {
    try {
      // play() predates its promise return, and jsdom's stub hands back undefined.
      await Promise.resolve(audioElement.play())
      transportTrace.info("audio_playback_started")
    } catch (error) {
      const errorName = error instanceof Error ? error.name : "unknown"
      transportTrace.warn("audio_playback_failed", {
        errorName,
        message: errorMessage(error, "Could not start voice audio playback."),
      })
      // A refused session cannot be heard, so fail it rather than hand back a
      // healthy-looking silent call. AbortError is a session already torn down.
      if (errorName === "NotAllowedError") {
        this.failConnection("audio_playback_blocked")
      }
    }
  }

  private unsubscribeVoiceChat() {
    this.removeSocketConnectedCallback?.()
    this.removeSocketConnectedCallback = null
    const handler = this.voiceChatEventHandler
    if (handler) {
      ObjectSubscriptionManager.instance.unsubscribe<VoiceChatEvent>(handler)
      this.voiceChatEventHandler = null
    }
  }

  private handleMessage(rawMessage: unknown) {
    if (typeof rawMessage !== "string") return

    const event = parseVoiceRealtimeEvent(rawMessage)
    if (!event) {
      pipelineTrace.warn("realtime_event_dropped", {
        reason: "parse_returned_null",
        payloadLength: realtimePayloadLength(rawMessage),
      })
      return
    }

    this.handleEvent(event)
  }

  // Capture pre-transition authority before synchronously projecting normalized
  // transitions into Redux. `afterTransitions` then uses both that capture and the
  // updated projection for persistence, audio, and tool side effects.
  private handleEvent(rawEvent: VoiceRealtimeEvent) {
    // Decided on the RAW event: only the pause's own truncation gets the
    // keep-the-dimmed-remainder treatment; an aliased barge-in truncation of
    // the continuation is indistinguishable after the rewrite.
    this.handlingPauseTruncation =
      rawEvent.type === "conversation.item.truncated" &&
      rawEvent.item_id === this.pendingPauseTruncationItemId
    if (this.handlingPauseTruncation) {
      this.pendingPauseTruncationItemId = null
    }
    setVoiceTraceEventSeq(++this.eventSeq)
    try {
      // Before anything else — trace, checkpoints, transitions, and side
      // effects must all see one consistent identity for a merged
      // continuation item. Inside the try: it writes maps and traces, and a
      // throw from out here left `handlingPauseTruncation` latched on for the
      // rest of the session, suppressing every later caption trim.
      const event = this.aliasPauseContinuationEvent(rawEvent)
      this.flushTranscriptDeltaBeforeTerminal(event)
      this.traceIn(event)
      const capture = this.beforeTransitions(event)
      if (capture.suppressed) return

      toTurnTransitions(event)
        .filter((transition) => !this.transitionSuppressedForPause(transition))
        .forEach(this.deps.onTurnTransition)
      const updatedVoiceState = this.traceApplied(event)
      this.traceTranscriptDeltaCheckpoint(event, updatedVoiceState)
      this.afterTransitions(event, updatedVoiceState, capture)
    } finally {
      this.handlingPauseTruncation = false
      setVoiceTraceEventSeq(null)
    }
  }

  private stateDigest(state: VoiceSessionStateSnapshot) {
    return JSON.stringify(voiceRealtimeDebugState(state))
  }

  private traceIn(event: VoiceRealtimeEvent) {
    const state = this.deps.getVoiceState()
    const digest = this.stateDigest(state)
    if (this.isTranscriptDeltaTraceEvent(event)) return

    recordVoiceTrace({
      category: "pipeline",
      level: "info",
      direction: "in",
      summary: event.type,
      drift: this.lastPostDigest !== null && digest !== this.lastPostDigest,
      detail: {
        ...voiceRealtimeDebugEvent(event),
      },
    })
  }

  private traceApplied(event: VoiceRealtimeEvent) {
    const updatedVoiceState = this.deps.getVoiceState()
    if (!this.isTranscriptDeltaTraceEvent(event)) {
      recordVoiceTrace({
        category: "pipeline",
        level: "info",
        summary: "realtime_event_applied",
        detail: {
          ...voiceRealtimeDebugEvent(event),
          state: voiceRealtimeDebugState(updatedVoiceState),
        },
      })
    }
    this.lastPostDigest = this.stateDigest(updatedVoiceState)
    return updatedVoiceState
  }

  private isTranscriptDeltaTraceEvent(
    event: VoiceRealtimeEvent
  ): event is TranscriptDeltaTraceEvent {
    return (
      event.type === "response.output_audio_transcript.delta" ||
      event.type === "conversation.item.input_audio_transcription.delta"
    )
  }

  private transcriptDeltaCheckpointKey(event: TranscriptDeltaTraceEvent) {
    const itemId = "item_id" in event ? (event.item_id ?? null) : null
    return `${event.type}:${itemId ?? "missing"}`
  }

  private traceTranscriptDeltaCheckpoint(
    event: VoiceRealtimeEvent,
    updatedVoiceState: VoiceSessionStateSnapshot
  ) {
    if (!this.isTranscriptDeltaTraceEvent(event)) return

    const checkpointKey = this.transcriptDeltaCheckpointKey(event)
    const now = nowMillis()
    const itemId = "item_id" in event ? (event.item_id ?? null) : null
    const responseId =
      event.type === "response.output_audio_transcript.delta"
        ? event.response_id
        : null
    const item = voiceLiveItemEntries(updatedVoiceState).find(
      (entry) => entry.item.itemId === itemId
    )?.item
    const existing = this.transcriptDeltaCheckpoints.get(checkpointKey)
    const checkpoint: TranscriptDeltaTraceCheckpoint = {
      type: event.type,
      itemId,
      responseId,
      hasEmitted: existing?.hasEmitted ?? false,
      deltaCount: (existing?.deltaCount ?? 0) + 1,
      deltaChars: (existing?.deltaChars ?? 0) + event.delta.length,
      eventSeqStart:
        existing && existing.deltaCount > 0
          ? existing.eventSeqStart
          : this.eventSeq,
      eventSeqEnd: this.eventSeq,
      lastEmittedAt: existing?.lastEmittedAt ?? 0,
      latestTextLen: item?.text.length ?? 0,
      latestRevealedLen: item?.revealedLength ?? 0,
      state: voiceRealtimeDebugState(updatedVoiceState),
    }

    this.transcriptDeltaCheckpoints.set(checkpointKey, checkpoint)
    if (
      !checkpoint.hasEmitted ||
      now - checkpoint.lastEmittedAt >= TRANSCRIPT_DELTA_TRACE_CHECKPOINT_MS
    ) {
      this.emitTranscriptDeltaCheckpoint(checkpointKey, now)
    }
  }

  private flushTranscriptDeltaBeforeTerminal(event: VoiceRealtimeEvent) {
    if (
      event.type !== "response.output_audio_transcript.done" &&
      event.type !== "conversation.item.input_audio_transcription.completed"
    ) {
      return
    }

    const deltaType =
      event.type === "response.output_audio_transcript.done"
        ? "response.output_audio_transcript.delta"
        : "conversation.item.input_audio_transcription.delta"
    const checkpointKey = `${deltaType}:${event.item_id}`
    this.emitTranscriptDeltaCheckpoint(checkpointKey, nowMillis())
  }

  private emitTranscriptDeltaCheckpoint(checkpointKey: string, now: number) {
    const checkpoint = this.transcriptDeltaCheckpoints.get(checkpointKey)
    if (!checkpoint) return
    if (checkpoint.deltaCount === 0) return

    const activeEventSeq = currentTraceEventSeq()
    setVoiceTraceEventSeq(checkpoint.eventSeqEnd)
    recordVoiceTrace({
      category: "pipeline",
      level: "info",
      direction: "in",
      summary: checkpoint.type,
      eventSeq: checkpoint.eventSeqEnd,
      detail: {
        type: checkpoint.type,
        itemId: checkpoint.itemId,
        responseId: checkpoint.responseId,
        deltaCount: checkpoint.deltaCount,
        deltaChars: checkpoint.deltaChars,
        eventSeqStart: checkpoint.eventSeqStart,
        eventSeqEnd: checkpoint.eventSeqEnd,
        textLen: checkpoint.latestTextLen,
        revealedLen: checkpoint.latestRevealedLen,
        state: checkpoint.state,
      },
    })
    setVoiceTraceEventSeq(activeEventSeq)

    this.transcriptDeltaCheckpoints.set(checkpointKey, {
      ...checkpoint,
      hasEmitted: true,
      deltaCount: 0,
      deltaChars: 0,
      eventSeqStart: checkpoint.eventSeqEnd,
      lastEmittedAt: now,
    })
  }

  private clientEventTraceDetail(event: VoiceRealtimeClientEvent) {
    if (
      event.type === "conversation.item.create" &&
      typeof event.item.id === "string" &&
      event.item.id.startsWith("mindtrip_history_")
    ) {
      const item = event.item
      if (item.type === "message") {
        return {
          type: event.type,
          item: {
            id: item.id,
            type: item.type,
            role: item.role,
            content: item.content.map((content) => {
              if (content.type === "input_image") {
                return {
                  type: content.type,
                  imageUrlLength: content.image_url.length,
                  detail: content.detail,
                }
              }
              return {
                type: content.type,
                textLength: content.text.length,
              }
            }),
          },
        }
      }
      if (item.type === "function_call") {
        return {
          type: event.type,
          item: {
            id: item.id,
            type: item.type,
            name: item.name,
            call_id: item.call_id,
            argumentLength: item.arguments.length,
          },
        }
      }
      return {
        type: event.type,
        item: {
          id: item.id,
          type: item.type,
          call_id: item.call_id,
          outputLength: item.output.length,
        },
      }
    }

    if (
      event.type === "conversation.item.create" &&
      "event_id" in event &&
      "id" in event.item &&
      event.item.type === "message" &&
      event.item.id.startsWith("item_mindtrip_tool_media_")
    ) {
      return {
        type: event.type,
        eventId: event.event_id,
        item: {
          id: event.item.id,
          type: event.item.type,
          role: event.item.role,
          mediaCount: event.item.content.length,
        },
      }
    }

    if (event.type === "response.create") {
      return {
        type: event.type,
        eventId: event.event_id,
        response: {
          conversation: event.response.conversation ?? null,
          outputModalities: event.response.output_modalities ?? null,
          metadata: event.response.metadata,
          toolNames: event.response.tools?.map(({ name }) => name) ?? [],
          toolChoice: event.response.tool_choice?.name ?? null,
          inputLengths:
            event.response.input?.map((item) =>
              item.type === "message"
                ? item.content.map(({ text }) => text.length)
                : null
            ) ?? [],
        },
      }
    }

    if (
      event.type !== "conversation.item.create" ||
      event.item.type !== "function_call_output"
    ) {
      return event
    }

    const outputMetadata = this.toolOutputTraceMetadata(event.item.output)
    return {
      type: event.type,
      item: {
        type: event.item.type,
        id: event.item.id,
        call_id: event.item.call_id,
        outputPrefix: outputMetadata.outputPrefix,
        outputLength: outputMetadata.outputLength,
        outputKind: outputMetadata.outputKind,
        outputKeys: outputMetadata.outputKeys,
      },
    }
  }

  private toolOutputTraceMetadata(output: string) {
    let outputKind = "string"
    let outputKeys: string[] = []
    try {
      const parsed: unknown = JSON.parse(output)
      if (isRecord(parsed)) {
        outputKind = "object"
        outputKeys = Object.keys(parsed)
      } else if (Array.isArray(parsed)) {
        outputKind = "array"
      } else if (parsed === null) {
        outputKind = "null"
      } else {
        outputKind = typeof parsed
      }
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error
    }

    return {
      outputPrefix: output.slice(0, TOOL_OUTPUT_TRACE_PREFIX_LENGTH),
      outputLength: output.length,
      outputKind,
      outputKeys,
    }
  }

  private beforeTransitions(event: VoiceRealtimeEvent): EventPipelineCapture {
    const state = this.deps.getVoiceState()
    const latestUserItemId = this.latestUserItemId(state)
    const capture: EventPipelineCapture = {
      suppressed: false,
      userItemId: null,
      latestUserItemId: null,
      fullTranscriptBeforeTruncation: null,
      audibleItemsAtBoundary: null,
    }

    const responseId = realtimeEventResponseId(event)
    if (
      responseId &&
      (this.placeAnnotationStateForResponse(responseId) !== undefined ||
        this.suppressedPlaceAnnotationResponseIds.has(responseId))
    ) {
      const itemId = realtimeEventItemId(event)
      if (itemId) {
        this.suppressedPlaceAnnotationItemIds.add(itemId)
      }
    }

    if (
      event.type === "conversation.item.created" ||
      event.type === "conversation.item.added" ||
      event.type === "conversation.item.done"
    ) {
      if (this.suppressedPlaceAnnotationItemIds.has(event.item.id)) {
        if (event.type === "conversation.item.done") {
          this.suppressedPlaceAnnotationItemIds.delete(event.item.id)
        }
        capture.suppressed = true
        return capture
      }
      // The pause-resume directive never joins the annotation context: it is
      // retracted (conversation.item.delete) once the continuation finishes,
      // and an item_reference to a deleted item fails the whole annotation
      // response with invalid_reference_item_id — the stale id would sit in
      // this list and kill every later annotation in the session.
      if (
        !event.item.id.startsWith(PAUSE_DIRECTIVE_ITEM_PREFIX) &&
        !this.placeAnnotationContextItemIds.includes(event.item.id)
      ) {
        this.placeAnnotationContextItemIds.push(event.item.id)
      }
    }

    if (
      event.type === "response.output_text.delta" ||
      event.type === "response.output_text.done"
    ) {
      const annotationState = this.placeAnnotationStateForResponse(
        event.response_id
      )
      if (annotationState) {
        capture.suppressed = true
        if (event.type === "response.output_text.delta") {
          this.appendPlaceAnnotationText(annotationState, event.delta)
        } else {
          this.finalizePlaceAnnotationText(annotationState, event.text)
        }
        return capture
      }
    }

    if (event.type === "error") {
      const annotationState = this.placeAnnotationStateForError(event)
      if (annotationState) {
        this.cancelPlaceAnnotationExtraction(annotationState, "error")
        capture.suppressed = true
        return capture
      }
      if (
        event.response_id &&
        this.suppressedPlaceAnnotationResponseIds.has(event.response_id)
      ) {
        capture.suppressed = true
        return capture
      }
    }

    // Ahead of the staleness suppression below, which is what drops this
    // transcript for a cancelled response.
    if (event.type === "response.output_audio_transcript.done") {
      this.extendPauseCutFromTerminalTranscript(event)
    }

    switch (event.type) {
      case "session.updated":
        this.acknowledgeSkillUpdate(event.session.tools)
        break
      case "response.content_part.added":
      case "response.output_audio_transcript.delta":
      case "response.output_audio_transcript.done":
      case "response.output_item.added":
      case "response.output_item.done":
      case "response.output_text.delta":
      case "response.output_text.done":
      case "response.function_call_arguments.done":
      case "output_audio_buffer.started":
      case "output_audio_buffer.stopped":
      case "output_audio_buffer.cleared":
        if (
          event.response_id &&
          (this.placeAnnotationStateForResponse(event.response_id) !==
            undefined ||
            this.suppressedPlaceAnnotationResponseIds.has(event.response_id) ||
            this.gate.isResponseSuppressed(event.response_id))
        ) {
          capture.suppressed = true
          return capture
        }
        break
      case "response.done": {
        const responseId = event.response?.id
        if (
          responseId &&
          (this.placeAnnotationStateForResponse(responseId) ||
            this.suppressedPlaceAnnotationResponseIds.has(responseId))
        ) {
          this.finishPlaceAnnotationResponse(responseId, event.response?.status)
          capture.suppressed = true
          return capture
        }
        if (responseId && this.gate.isResponseSuppressed(responseId)) {
          this.gate.responseDone(responseId)
          this.responseAudioStates.delete(responseId)
          capture.suppressed = true
          return capture
        }
        break
      }
      default:
        break
    }

    switch (event.type) {
      case "input_audio_buffer.speech_started": {
        this.deps.onCustomerSpeechStarted?.()
        // Before the gate sees the speech: a paused gate would hold the turn
        // this speech is about to produce.
        const endedPause = this.paused
        this.abandonPauseForUserSpeech()
        // The pause already recorded `user_stopped`, and the truncation it
        // asked for can still be in flight: overwriting the reason made that
        // write, and the trace built on it, report the pause as a barge-in.
        if (!endedPause) {
          this.cutReason = "user_interruption"
        }
        this.gate.userSpeechStarted()
        // A customer talking makes the on-connect greeting redundant. If we
        // haven't sent it yet, skip it (see sendGreeting); if it's already
        // playing, Realtime's `interrupt_response` cancels it server side.
        this.greetingSuperseded = true
        // Speech before the final history item is acknowledged means this turn
        // may be answered without prior context.
        if (this.historyReplay.acknowledgement !== null) {
          transportTrace.warn("user_speech_before_history_ready", {
            elapsedMs:
              performance.now() - this.historyReplay.acknowledgement.startedAt,
          })
        }
        // Remember speech that can cancel an assistant response: if its
        // transcript later comes back empty it was noise/echo, and the reply
        // it cancelled may need a resume nudge (see persistUserTurn).
        // Speech that ended a pause counts: it just dropped a remainder
        // nothing else is going to speak, so a cough that ends a pause must
        // not leave the answer stranded in dead air.
        if (
          event.item_id &&
          (endedPause ||
            this.gate.activeResponseId !== null ||
            state.connectionStatus === "assistant_speaking")
        ) {
          this.interruptingUserItems.set(event.item_id, {
            assistantAudioWasAudible: state.audibleAssistantResponseId !== null,
            audioStartMs: event.audio_start_ms,
          })
        }
        break
      }
      case "input_audio_buffer.speech_stopped": {
        const interruption = this.interruptingUserItems.get(event.item_id)
        if (
          interruption?.assistantAudioWasAudible &&
          event.audio_end_ms - interruption.audioStartMs < SHORT_BARGE_IN_MAX_MS
        ) {
          this.deps.onShortBargeIn?.()
        }
        this.gate.userSpeechStopped()
        break
      }
      case "response.function_call_arguments.done":
        capture.userItemId = this.userItemIdForToolEvent(
          event,
          latestUserItemId
        )
        break
      case "response.created":
        capture.latestUserItemId = latestUserItemId
        if (event.response.metadata?.mindtrip_trigger === "place_annotation") {
          this.registerPlaceAnnotationResponse(
            event.response.id,
            event.response.metadata
          )
          capture.suppressed = true
          return capture
        }
        capture.suppressed = this.responseCreated(
          event.response.id,
          latestUserItemId,
          event.response.metadata?.mindtrip_trigger
        )
        break
      case "conversation.item.truncated":
        capture.fullTranscriptBeforeTruncation =
          voiceLiveItemEntries(state).find(
            (entry) =>
              entry.item.itemId === event.item_id &&
              entry.item.role === "assistant"
          )?.item.text ?? null
        break
      case "error":
        if ((event.error?.event_id ?? event.event_id) === "voice_greeting") {
          this.releaseInitialResponseMic("greeting_error")
        }
        if (
          (event.error?.event_id ?? event.event_id)?.startsWith(
            "voice_history_"
          )
        ) {
          transportTrace.warn("history_ack_failed", {
            eventId: event.error?.event_id ?? event.event_id,
            reason: "realtime_error",
          })
          capture.suppressed = true
          break
        }
        this.rejectSkillUpdate(event.error?.event_id)
        capture.suppressed =
          this.toolResultRelay.handleError(event) ||
          this.gate.handleError(event)
        // Unclaimed but benign (see isFatalRealtimeError): the provider
        // rejected one client event while the session keeps running, so
        // projecting it would end a working call.
        if (!capture.suppressed && !isFatalRealtimeError(event)) {
          const code = event.error?.code ?? event.code ?? null
          transportTrace.warn("provider_error_benign", {
            code,
            errorType: event.error?.type ?? null,
            message: event.error?.message ?? event.message ?? null,
          })
          this.deps.onBenignProviderError?.(code)
          capture.suppressed = true
          break
        }
        if (!capture.suppressed) {
          this.stopSpeechGateLoop()
          this.resetAssistantAudioStalled()
        }
        break
      case "conversation.item.input_audio_transcription.failed":
        this.persistQueue.rejectOutstanding("transcription_failed")
        break
      case "response.content_part.added":
        if (event.part.type === "audio") {
          this.responseAudioStates.set(event.response_id, "generated")
        }
        break
      case "output_audio_buffer.started":
        this.responseAudioStates.set(event.response_id, "started")
        this.audioBufferActive = true
        // A `started` while the gate window is still open is an item
        // boundary inside the same response (or a gapless continuation), not
        // a new playback window — the buffer never drained, no `stopped`
        // preceded it. Resetting the meter and gate here split one window's
        // audio across two half-measured ones: round-14 measured a
        // filler+narration turn ~12% fast (both items' units over only the
        // second item's speaking seconds) and voice-trace-7369273's two-item
        // narration lost its rate sample entirely. Skipping the reset also
        // keeps a measured item-gap silence latched instead of force-flipping
        // the gate to speaking. Known residual: a LOST `stopped` followed by
        // a genuinely new window merges two turns into one sample;
        // duration-weighted buckets bound that damage.
        if (this.speechGateTimer === null) {
          this.assistantAudioWindowStartedAtMs = nowMillis()
          this.captionMeter.audioStarted(event.response_id)
          this.startSpeechGateWindow(event.response_id)
        } else {
          captionsTrace.info("caption_gate_window_continued", {
            responseId: event.response_id,
            startResponseId: this.audibleWindowStartResponseId,
            speakingSeconds: this.audibleWindowSpeakingSeconds,
          })
        }
        break
      case "output_audio_buffer.stopped":
      case "output_audio_buffer.cleared":
        capture.audibleItemsAtBoundary = this.audibleWindowItemsAtBoundary(
          state,
          event.response_id
        )
        capture.suppressed = capture.audibleItemsAtBoundary === null
        break
      default:
        break
    }

    return capture
  }

  private afterTransitions(
    event: VoiceRealtimeEvent,
    updatedVoiceState: VoiceSessionStateSnapshot,
    capture: EventPipelineCapture
  ) {
    switch (event.type) {
      case "output_audio_buffer.started":
        this.assistantAudioStartedAt = nowMillis()
        this.flushStartedAssistantTurns()
        break
      case "output_audio_buffer.stopped":
        this.assistantAudioStartedAt = null
        this.stopSpeechGateLoop()
        this.resetAssistantAudioStalled()
        this.reportAssistantAudioWindow()
        this.captionMeter.audioStopped(
          event.response_id,
          capture.audibleItemsAtBoundary ?? [],
          this.audibleWindowGated
            ? { seconds: this.audibleWindowSpeakingSeconds }
            : null
        )
        this.responseAudioStates.set(event.response_id, "stopped")
        this.flushStartedAssistantTurns()
        this.audioBufferActive = false
        this.gate.audioStopped(event.response_id)
        this.flushNoAudioAssistantTurns()
        this.flushOutputSpeed()
        this.releaseInitialResponseMic("response_finished", event.response_id)
        break
      case "output_audio_buffer.cleared":
        this.stopSpeechGateLoop()
        this.resetAssistantAudioStalled()
        this.reportAssistantAudioWindow()
        // Release only the optional opening hold; explicit mute and the
        // closing-response gate still apply.
        this.releaseInitialResponseMic("response_finished", event.response_id)
        this.audioBufferActive = false
        capture.audibleItemsAtBoundary?.forEach((item) => {
          this.clearedAssistantItemIds.add(item.itemId)
        })
        this.gate.audioCleared(
          event.response_id,
          this.assistantAudioStartedAt === null
            ? 0
            : nowMillis() - this.assistantAudioStartedAt
        )
        this.assistantAudioStartedAt = null
        this.captionMeter.audioCleared()
        break
      case "conversation.item.truncated": {
        // Ultra-fast resume: the pause's own truncation can land after the
        // continuation has already attached and reopened this item. Marking
        // it interrupted/settled now would block the merged turn's finalized
        // persist, and the heard-prefix write would be staler than the live
        // merged text — skip both; the continuation owns the item again.
        if (
          this.handlingPauseTruncation &&
          this.pauseContinuation?.responseId != null
        ) {
          break
        }
        this.interruptedAssistantItemIds.add(event.item_id)
        this.settledAssistantItemIds.add(event.item_id)
        this.stagedAssistantTurns.delete(event.item_id)
        // A pause's truncation is not a barge-in: the resume re-speaks the cut
        // remainder, so the customer does hear it.
        const bargeIn = this.cutReason !== "user_stopped"
        const heardTranscript = this.persistInterruptedAssistantTurn(
          event,
          updatedVoiceState,
          capture.fullTranscriptBeforeTruncation
        )
        if (bargeIn) {
          this.sendCutOffNote(heardTranscript)
        }
        this.clearedAssistantItemIds.delete(event.item_id)
        this.flushNoAudioAssistantTurns()
        break
      }
      case "conversation.item.input_audio_transcription.completed":
        this.persistUserTurn(event, updatedVoiceState)
        break
      case "conversation.item.created":
      case "conversation.item.added":
      case "conversation.item.done":
      case "conversation.item.retrieved":
        this.toolResultRelay.acknowledgeItem(event.item.id)
        this.acknowledgeHistoryItem(event.item.id)
        break
      case "response.output_audio_transcript.done": {
        pipelineTrace.info("response.output_audio_transcript.done", {
          timestamp: voiceClockNow(),
          providerEventId: event.item_id,
        })
        const toolCallIds = this.toolCallIdsForAssistantItem(
          event.item_id,
          updatedVoiceState,
          event.transcript
        )
        this.stagePlaceAnnotation(event, updatedVoiceState)
        this.stageFinalizedAssistantTurn(event, updatedVoiceState, toolCallIds)
        break
      }
      case "response.output_item.added": {
        if (event.item.type !== "message" || event.item.role !== "assistant") {
          break
        }
        // Arrival order, so a merged turn's ORIGINAL response — the one that
        // ran the tools — stays ahead of the continuations that own none.
        const responseIds =
          this.assistantItemResponseIds.get(event.item.id) ?? []
        if (!responseIds.includes(event.response_id)) {
          responseIds.push(event.response_id)
        }
        this.assistantItemResponseIds.set(event.item.id, responseIds)
        break
      }
      case "response.output_item.done":
        this.completePlaceAnnotationSourceItem(event)
        break
      case "response.created":
        // Attached AFTER the responseStarted projection on purpose: the
        // paused item must still be `interrupted` while that reducer's
        // snap-prior-turns pass runs, or the snap would reveal its dimmed
        // remainder before the continuation has said a word.
        if (event.response.metadata?.mindtrip_trigger === "pause_resume") {
          this.attachPauseContinuation(event.response.id)
        }
        // Prefer the explicit greeting tag; the no-user-turn fallback covers
        // responses without metadata. Replayed history may include user turns.
        if (
          this.greetingSent &&
          this.greetingResponseId === null &&
          (event.response.metadata?.mindtrip_trigger === "greeting" ||
            capture.latestUserItemId === null)
        ) {
          this.greetingResponseId = event.response.id
          pipelineTrace.info("greeting_response_tagged", {
            responseId: event.response.id,
          })
        }
        break
      case "response.done": {
        const responseId = event.response?.id ?? null
        if (responseId && this.pauseContinuation?.responseId === responseId) {
          // The continuation finished; the aliases it registered stay (late
          // events for its items must keep landing on the merged turn), but a
          // NEXT pause starts its own cycle from the merged item.
          this.pauseContinuation = null
          this.deletePauseDirectiveItem()
        }
        if (responseId) {
          this.closeGraphqlBatch(
            responseId,
            event.response?.status === "completed"
          )
          const audioState = this.responseAudioStates.get(responseId)
          const noAudio = audioState === undefined || audioState === "no_audio"
          if (
            (event.response?.status === "cancelled" ||
              event.response?.status === "failed") &&
            audioState !== "started" &&
            audioState !== "stopped" &&
            !this.paused &&
            this.pausedRemainder === null
          ) {
            this.dropUnheardAssistantTurns(responseId, updatedVoiceState)
          }
          // Generation can finish before playback even starts. Only an empty
          // or failed/cancelled response without active audio can release here;
          // normal speech waits for the buffer's stopped/cleared event.
          if (
            noAudio ||
            (!this.audioBufferActive &&
              (event.response?.status === "failed" ||
                event.response?.status === "cancelled"))
          ) {
            this.releaseInitialResponseMic("response_finished", responseId)
          }
          this.gate.responseDone(
            responseId,
            noAudio,
            event.response?.status === "cancelled"
          )
          if (audioState === "generated") {
            this.responseAudioStates.delete(responseId)
          } else if (audioState === undefined) {
            this.responseAudioStates.set(responseId, "no_audio")
            this.flushNoAudioAssistantTurns()
          }
          this.completePlaceAnnotationSourceResponse(event)
        } else {
          this.gate.responseDone(null)
        }
        this.flushOutputSpeed()
        break
      }
      case "response.function_call_arguments.done":
        void this.handleToolCall(event, capture.userItemId)
        break
      default:
        break
    }
  }

  private toolCallIdsForAssistantItem(
    itemId: string,
    state: VoiceSessionStateSnapshot,
    transcript: string
  ) {
    if (transcript.trim().length === 0) return undefined

    const item = voiceLiveItemEntries(state).find(
      (entry) => entry.item.itemId === itemId
    )?.item
    if (!item?.responseId) return undefined

    // Every response that fed this turn, not just the one it is revealing
    // against: a pause-resume continuation takes the row's response identity
    // over (the emptied delta flips it), so reading ownership off that
    // identity attributed a merged turn's search results to a `pause_resume`
    // response that owns none. The annotation then went out with no tool call
    // ids, leaving Rails nothing to resolve the spoken names against — the
    // paused turn in voice-trace chat 9211597 sent 4 candidates and got 0
    // place mentions back, so the search results never appeared.
    const responseIds = this.assistantItemResponseIds.get(itemId) ?? [
      item.responseId,
    ]
    const callIds = responseIds.flatMap((responseId) => {
      const ownership = this.responseToolOwnership.get(responseId)
      if (!ownership) return []
      if (ownership.ownerItemId === null) {
        ownership.ownerItemId = itemId
      }
      return ownership.ownerItemId === itemId ? ownership.callIds : []
    })
    return callIds.length > 0 ? [...new Set(callIds)] : undefined
  }

  private stagePlaceAnnotation(
    event: Extract<
      VoiceRealtimeEvent,
      { type: "response.output_audio_transcript.done" }
    >,
    state: VoiceSessionStateSnapshot
  ) {
    if (!this.deps.onPlaceAnnotationCandidates) {
      return
    }
    if (this.closed || !this.openaiCallId) {
      return
    }
    if (event.transcript.trim().length === 0) {
      return
    }
    if (this.isGreetingTurn(event.item_id, state)) {
      return
    }
    if (this.placeAnnotationStates.has(event.item_id)) {
      pipelineTrace.info("voice_place_annotation_duplicate_skipped", {
        providerEventId: event.item_id,
      })
      return
    }

    // A merged turn's spoken text spans its real provider items; the source
    // window must reach the LATEST of them (the merged id alone resolves to
    // the truncated heard prefix server-side).
    const sourceItemIndex = [
      event.item_id,
      ...(this.mergedTurnProviderIds.get(event.item_id) ?? []),
    ].reduce(
      (latest, id) =>
        Math.max(latest, this.placeAnnotationContextItemIds.indexOf(id)),
      -1
    )
    if (sourceItemIndex === -1) {
      pipelineTrace.warn("voice_place_annotation_failed", {
        providerEventId: event.item_id,
        reason: "source_context_missing",
      })
      return
    }

    this.placeAnnotationRequestCounter += 1
    const requestEventId = `voice_place_annotation_create_${this.placeAnnotationRequestCounter}`
    const annotationState: PlaceAnnotationState = {
      providerEventId: event.item_id,
      sourceResponseId: event.response_id,
      openaiCallId: this.openaiCallId,
      transcript: event.transcript,
      input: this.placeAnnotationContextItemIds
        .slice(0, sourceItemIndex + 1)
        .map((id) => ({ type: "item_reference", id })),
      requestEventId,
      transcriptDoneAt: voiceClockNow(),
      responseId: null,
      validationRetryCount: 0,
      status: "staged",
      itemComplete: false,
      rawText: "",
      candidates: [],
      deliveryTail: Promise.resolve(),
      transcriptSave: this.persistQueue.persistenceFor(event.item_id),
      timeoutId: null,
    }
    this.placeAnnotationStates.set(event.item_id, annotationState)
  }

  private completePlaceAnnotationSourceItem(
    event: Extract<VoiceRealtimeEvent, { type: "response.output_item.done" }>
  ) {
    if (event.item.type !== "message" || event.item.role !== "assistant") {
      return
    }

    const annotationState = this.placeAnnotationStates.get(event.item.id)
    if (annotationState?.sourceResponseId !== event.response_id) {
      return
    }
    annotationState.itemComplete = true
  }

  private completePlaceAnnotationSourceResponse(
    event: Extract<VoiceRealtimeEvent, { type: "response.done" }>
  ) {
    const responseId = event.response?.id
    if (!responseId) {
      return
    }

    const stagedAnnotations = [...this.placeAnnotationStates.values()].filter(
      (annotationState) =>
        annotationState.sourceResponseId === responseId &&
        annotationState.status === "staged"
    )
    if (stagedAnnotations.length === 0) {
      return
    }

    const output = event.response?.output
    if (event.response?.status !== "completed" || !output) {
      stagedAnnotations.forEach((annotationState) => {
        this.cleanupPlaceAnnotation(annotationState)
      })
      return
    }

    let finalAssistantItemId: string | null = null
    for (let index = output.length - 1; index >= 0; index -= 1) {
      const item = output[index]
      if (item.type === "message" && item.role === "assistant") {
        finalAssistantItemId = item.id
        break
      }
    }

    const selectedAnnotation = stagedAnnotations.find(
      (annotationState) =>
        annotationState.providerEventId === finalAssistantItemId
    )
    if (!selectedAnnotation?.itemComplete) {
      stagedAnnotations.forEach((annotationState) => {
        this.cleanupPlaceAnnotation(annotationState)
      })
      return
    }

    stagedAnnotations.forEach((annotationState) => {
      if (annotationState !== selectedAnnotation) {
        this.cleanupPlaceAnnotation(annotationState)
      }
    })

    const ownership = this.responseToolOwnership.get(responseId)
    if (ownership) {
      selectedAnnotation.toolCallIds = [...ownership.callIds]
    }
    this.sendPlaceAnnotation(selectedAnnotation)
  }

  private sendPlaceAnnotation(
    annotationState: PlaceAnnotationState,
    validationError?: string
  ) {
    annotationState.status = "active"
    const event = { item_id: annotationState.providerEventId }
    pipelineTrace.info("response.create", {
      timestamp: voiceClockNow(),
      providerEventId: event.item_id,
    })
    const sent = this.send(
      voicePlaceAnnotationClientEvent(
        annotationState.requestEventId,
        annotationState.providerEventId,
        annotationState.input,
        validationError
      )
    )
    if (!sent) {
      if (annotationState.validationRetryCount > 0) {
        this.finalizePlaceAnnotation(annotationState, [])
      } else {
        this.cleanupPlaceAnnotation(annotationState)
      }
      pipelineTrace.warn("voice_place_annotation_failed", {
        providerEventId: annotationState.providerEventId,
        reason: "data_channel_send_failed",
      })
      return
    }
    annotationState.timeoutId = setTimeout(() => {
      const activeAnnotation = this.placeAnnotationStates.get(
        annotationState.providerEventId
      )
      if (activeAnnotation === annotationState) {
        this.cancelPlaceAnnotationExtraction(annotationState, "timeout")
      }
    }, PLACE_ANNOTATION_TIMEOUT_MS)

    pipelineTrace.info(
      "voice_place_annotation_started",
      {
        providerEventId: annotationState.providerEventId,
        requestEventId: annotationState.requestEventId,
        transcriptLength: annotationState.transcript.length,
        transcriptDoneToRequestMs:
          voiceClockNow() - annotationState.transcriptDoneAt,
      },
      "out"
    )
  }

  private registerPlaceAnnotationResponse(
    responseId: string,
    metadata: VoiceResponseMetadata
  ) {
    const providerEventId = metadata.provider_event_id
    const annotationState = providerEventId
      ? this.placeAnnotationStates.get(providerEventId)
      : undefined
    if (
      !annotationState ||
      annotationState.status !== "active" ||
      annotationState.responseId !== null
    ) {
      this.suppressedPlaceAnnotationResponseIds.add(responseId)
      pipelineTrace.warn("voice_place_annotation_failed", {
        responseId,
        reason: "response_correlation_missing",
      })
      return
    }

    annotationState.responseId = responseId
    pipelineTrace.info("voice_place_annotation_response_created", {
      providerEventId: annotationState.providerEventId,
      responseId,
      transcriptDoneToResponseCreatedMs:
        voiceClockNow() - annotationState.transcriptDoneAt,
    })
  }

  private placeAnnotationStateForResponse(responseId: string | undefined) {
    if (!responseId) {
      return undefined
    }
    return [...this.placeAnnotationStates.values()].find(
      (annotationState) => annotationState.responseId === responseId
    )
  }

  private placeAnnotationStateForError(
    event: Extract<VoiceRealtimeEvent, { type: "error" }>
  ) {
    if (event.response_id) {
      return this.placeAnnotationStateForResponse(event.response_id)
    }

    const eventId = event.event_id ?? event.error?.event_id
    if (!eventId) {
      return undefined
    }
    return [...this.placeAnnotationStates.values()].find(
      (annotationState) => annotationState.requestEventId === eventId
    )
  }

  private cleanupPlaceAnnotation(annotationState: PlaceAnnotationState) {
    if (annotationState.timeoutId !== null) {
      clearTimeout(annotationState.timeoutId)
      annotationState.timeoutId = null
    }
    this.placeAnnotationStates.delete(annotationState.providerEventId)
    if (annotationState.responseId) {
      this.suppressedPlaceAnnotationResponseIds.add(annotationState.responseId)
    }
  }

  private clearPlaceAnnotations() {
    this.placeAnnotationStates.forEach((state) => {
      if (state.timeoutId !== null) {
        clearTimeout(state.timeoutId)
      }
    })
    this.placeAnnotationStates.clear()
    this.placeAnnotationContextItemIds.length = 0
    this.suppressedPlaceAnnotationResponseIds.clear()
    this.suppressedPlaceAnnotationItemIds.clear()
    this.mergedAnnotationCandidates.clear()
  }

  private appendPlaceAnnotationText(
    annotationState: PlaceAnnotationState,
    delta: string
  ) {
    if (annotationState.status !== "active") {
      return
    }
    if (
      annotationState.rawText.length + delta.length >
      VOICE_PLACE_ANNOTATION_TSV_MAX_LENGTH
    ) {
      this.retryPlaceAnnotationExtraction(
        annotationState,
        "text_length_exceeded",
        new Error(
          `Place annotation document length exceeds the limit: expected at most ${VOICE_PLACE_ANNOTATION_TSV_MAX_LENGTH}, got ${annotationState.rawText.length + delta.length}.`
        ),
        annotationState.rawText + delta
      )
      return
    }

    annotationState.rawText += delta
    const completeText = annotationState.rawText.slice(
      0,
      annotationState.rawText.lastIndexOf("\n") + 1
    )
    if (!completeText.includes(VOICE_PLACE_ANNOTATION_TSV_HEADER)) {
      return
    }

    let candidates: VoicePlaceAnnotationCandidate[]
    try {
      candidates = parseVoicePlaceAnnotationTsv(completeText)
    } catch (error) {
      this.retryPlaceAnnotationExtraction(
        annotationState,
        "stream_text_invalid",
        error
      )
      return
    }
    if (candidates.length <= annotationState.candidates.length) {
      return
    }

    annotationState.candidates = candidates
    this.queuePlaceAnnotationDelivery(
      annotationState,
      mergePlaceAnnotationCandidates(
        this.mergedAnnotationCandidates.get(annotationState.providerEventId) ??
          [],
        candidates
      )
    )
  }

  private finalizePlaceAnnotationText(
    annotationState: PlaceAnnotationState,
    text: string
  ) {
    if (annotationState.status !== "active") {
      return
    }

    annotationState.rawText = text
    const normalizedText = text.endsWith("\n") ? text : `${text}\n`
    let candidates: VoicePlaceAnnotationCandidate[]
    try {
      candidates = parseVoicePlaceAnnotationTsv(normalizedText)
    } catch (error) {
      this.retryPlaceAnnotationExtraction(
        annotationState,
        "final_text_invalid",
        error
      )
      pipelineTrace.info("voice_place_annotation_arguments_done", {
        providerEventId: annotationState.providerEventId,
        responseId: annotationState.responseId,
        candidateCount: 0,
        transcriptDoneToTextMs:
          voiceClockNow() - annotationState.transcriptDoneAt,
      })
      return
    }

    this.finalizePlaceAnnotation(annotationState, candidates)
    pipelineTrace.info("voice_place_annotation_arguments_done", {
      providerEventId: annotationState.providerEventId,
      responseId: annotationState.responseId,
      candidateCount: candidates.length,
      transcriptDoneToTextMs:
        voiceClockNow() - annotationState.transcriptDoneAt,
    })
  }

  private finalizePlaceAnnotation(
    annotationState: PlaceAnnotationState,
    candidates: VoicePlaceAnnotationCandidate[]
  ) {
    if (annotationState.status === "final") {
      return
    }
    if (annotationState.timeoutId !== null) {
      clearTimeout(annotationState.timeoutId)
      annotationState.timeoutId = null
    }

    annotationState.candidates = candidates
    annotationState.status = "final"
    // A merged turn's fresh extraction saw only the continuation's remainder
    // item; the carried pre-pause candidates rejoin here so the replace-all
    // resolution covers the whole merged body.
    const finalCandidates = mergePlaceAnnotationCandidates(
      this.mergedAnnotationCandidates.get(annotationState.providerEventId) ??
        [],
      candidates
    )
    // Banked durably for every finalized annotation: the state self-deletes
    // once its delivery completes (the finally below), so a LATER pause's
    // attach would find nothing to carry — voice-trace chat 9211593's second
    // cycle delivered fresh-only candidates that resolved to nothing and
    // replace-all wiped the turn's places.
    this.mergedAnnotationCandidates.set(
      annotationState.providerEventId,
      finalCandidates
    )
    const pendingDelivery = annotationState.deliveryTail
    annotationState.deliveryTail = (async () => {
      try {
        await pendingDelivery
        const transcriptSaveUnchanged =
          await this.deliverPlaceAnnotationCandidates(
            annotationState,
            finalCandidates
          )
        if (
          this.placeAnnotationStates.get(annotationState.providerEventId) !==
            annotationState ||
          transcriptSaveUnchanged
        ) {
          return
        }

        await this.deliverPlaceAnnotationCandidates(
          annotationState,
          finalCandidates
        )
      } catch (error) {
        pipelineTrace.warn("voice_place_annotation_failed", {
          providerEventId: annotationState.providerEventId,
          reason: "persistence_failed",
          message: errorMessage(error, "Could not persist voice transcript."),
        })
      } finally {
        if (
          this.placeAnnotationStates.get(annotationState.providerEventId) ===
          annotationState
        ) {
          this.cleanupPlaceAnnotation(annotationState)
        }
      }
    })()
  }

  private retryPlaceAnnotationExtraction(
    annotationState: PlaceAnnotationState,
    reason: string,
    error: unknown,
    rawText = annotationState.rawText
  ) {
    const message = errorMessage(
      error,
      "Place annotation response failed validation."
    )
    const failedResponseId = annotationState.responseId
    const failedRawText = placeAnnotationFailureRawText(rawText)
    pipelineTrace.warn("voice_place_annotation_failed", {
      providerEventId: annotationState.providerEventId,
      responseId: failedResponseId,
      reason,
      message,
      rawText: failedRawText.text,
      rawTextTruncated: failedRawText.truncated,
    })
    if (failedResponseId) {
      if (reason !== "final_text_invalid") {
        this.send({
          type: "response.cancel",
          response_id: failedResponseId,
        })
      }
      this.suppressedPlaceAnnotationResponseIds.add(failedResponseId)
    }
    if (annotationState.validationRetryCount >= 1) {
      void this.reportPlaceAnnotationFailure(annotationState, {
        annotationResponseId: failedResponseId,
        validationError: message,
        rawText: failedRawText,
      })
      this.finalizePlaceAnnotation(annotationState, [])
      return
    }
    if (annotationState.timeoutId !== null) {
      clearTimeout(annotationState.timeoutId)
      annotationState.timeoutId = null
    }

    this.placeAnnotationRequestCounter += 1
    annotationState.validationRetryCount += 1
    annotationState.requestEventId = `voice_place_annotation_create_${this.placeAnnotationRequestCounter}`
    annotationState.responseId = null
    annotationState.rawText = ""
    annotationState.candidates = []
    this.sendPlaceAnnotation(annotationState, message)
  }

  private async reportPlaceAnnotationFailure(
    annotationState: PlaceAnnotationState,
    failure: {
      annotationResponseId: string | null
      validationError: string
      rawText: { text: string; truncated: boolean }
    }
  ) {
    try {
      await this.deps.reportPlaceAnnotationFailure?.({
        chatId: this.deps.chatId,
        openaiCallId: annotationState.openaiCallId,
        providerEventId: annotationState.providerEventId,
        annotationResponseId: failure.annotationResponseId ?? undefined,
        sourceResponseId: annotationState.sourceResponseId,
        validationError: failure.validationError,
        rawText: failure.rawText.text,
        rawTextTruncated: failure.rawText.truncated,
      })
    } catch {
      // Failure reporting must not affect the live voice session.
    }
  }

  private cancelPlaceAnnotationExtraction(
    annotationState: PlaceAnnotationState,
    reason: string,
    error?: unknown
  ) {
    if (annotationState.responseId && annotationState.status === "active") {
      const responseId = annotationState.responseId
      this.send({
        type: "response.cancel",
        response_id: responseId,
      })
      this.suppressedPlaceAnnotationResponseIds.add(responseId)
    }
    if (error) {
      pipelineTrace.warn("voice_place_annotation_failed", {
        providerEventId: annotationState.providerEventId,
        responseId: annotationState.responseId,
        reason,
        message: errorMessage(error, "Could not parse place annotation text."),
      })
    } else {
      pipelineTrace.warn("voice_place_annotation_failed", {
        providerEventId: annotationState.providerEventId,
        responseId: annotationState.responseId,
        reason,
      })
    }
    this.finalizePlaceAnnotation(annotationState, [])
  }

  private queuePlaceAnnotationDelivery(
    annotationState: PlaceAnnotationState,
    candidates: VoicePlaceAnnotationCandidate[]
  ) {
    const candidateSnapshot = [...candidates]
    const pendingDelivery = annotationState.deliveryTail
    annotationState.deliveryTail = (async () => {
      try {
        await pendingDelivery
        await this.deliverPlaceAnnotationCandidates(
          annotationState,
          candidateSnapshot
        )
      } catch (error) {
        pipelineTrace.warn("voice_place_annotation_failed", {
          providerEventId: annotationState.providerEventId,
          reason: "persistence_failed",
          message: errorMessage(error, "Could not persist voice transcript."),
        })
      }
    })()
  }

  private async deliverPlaceAnnotationCandidates(
    annotationState: PlaceAnnotationState,
    candidates: VoicePlaceAnnotationCandidate[]
  ) {
    let transcriptSave: Promise<VoiceTranscriptPersistenceResult>
    let persistence: VoiceTranscriptPersistenceResult
    do {
      transcriptSave = annotationState.transcriptSave
      persistence = await transcriptSave
    } while (transcriptSave !== annotationState.transcriptSave)

    if (
      this.closed ||
      this.placeAnnotationStates.get(annotationState.providerEventId) !==
        annotationState
    ) {
      return true
    }
    if (persistence.deleted === true) {
      this.cleanupPlaceAnnotation(annotationState)
      return true
    }

    try {
      await this.deps.onPlaceAnnotationCandidates?.({
        providerEventId: annotationState.providerEventId,
        messageId: persistence.messageId,
        openaiCallId: annotationState.openaiCallId,
        transcript: annotationState.transcript,
        toolCallIds: annotationState.toolCallIds,
        candidates,
      })
    } catch (error) {
      pipelineTrace.warn("voice_place_annotation_failed", {
        providerEventId: annotationState.providerEventId,
        reason: "candidate_callback_failed",
        message: errorMessage(error, "Could not annotate voice transcript."),
      })
    }
    return transcriptSave === annotationState.transcriptSave
  }

  private finishPlaceAnnotationResponse(
    responseId: string,
    status: string | undefined
  ) {
    const annotationState = this.placeAnnotationStateForResponse(responseId)
    if (!annotationState) {
      this.suppressedPlaceAnnotationResponseIds.delete(responseId)
      return
    }

    annotationState.responseId = null

    if (annotationState.status === "active") {
      if (status === "completed") {
        this.retryPlaceAnnotationExtraction(
          annotationState,
          "response_finished_without_final_text",
          new Error(
            "Place annotation response completed without final TSV text."
          )
        )
      } else {
        pipelineTrace.warn("voice_place_annotation_failed", {
          providerEventId: annotationState.providerEventId,
          responseId,
          reason: "response_finished_without_text",
        })
        this.finalizePlaceAnnotation(annotationState, [])
      }
    }

    this.suppressedPlaceAnnotationResponseIds.delete(responseId)
    if (status === "cancelled" || status === "incomplete") {
      pipelineTrace.info("voice_place_annotation_cancelled", {
        providerEventId: annotationState.providerEventId,
        responseId,
        status,
      })
    }
  }

  // Transcript completion only stages an assistant turn. Audio start authorizes
  // normal persistence in playback order; no-audio, interruption, and teardown
  // explicitly choose their separate terminal paths.
  private stageFinalizedAssistantTurn(
    event: Extract<
      VoiceRealtimeEvent,
      { type: "response.output_audio_transcript.done" }
    >,
    updatedVoiceState: VoiceSessionStateSnapshot,
    toolCallIds?: string[]
  ) {
    if (this.settledAssistantItemIds.has(event.item_id)) return

    this.terminalAssistantItemIds.add(event.item_id)
    if (
      this.interruptedAssistantItemIds.has(event.item_id) ||
      this.stagedAssistantTurns.has(event.item_id)
    ) {
      return
    }

    const finalizedTurn = finalizedTranscriptTurnForRealtimeEvent(
      event,
      updatedVoiceState
    )
    if (!finalizedTurn || !this.openaiCallId) {
      this.settleAssistantItem(event.item_id)
      this.flushNoAudioAssistantTurns()
      return
    }
    if (
      !this.deps.persistInitialResponse &&
      this.isGreetingTurn(finalizedTurn.providerEventId, updatedVoiceState)
    ) {
      persistTrace.info("greeting_persist_skipped", {
        providerEventId: finalizedTurn.providerEventId,
      })
      this.settleAssistantItem(finalizedTurn.providerEventId)
      this.flushNoAudioAssistantTurns()
      return
    }

    // A merged turn carries every contributing provider item id as
    // provenance (the backend stores them as provider_event_ids).
    const mergedProviderIds = this.mergedTurnProviderIds.get(event.item_id)
    const turn = {
      ...finalizedTurn,
      toolCallIds,
      ...(mergedProviderIds
        ? { providerEventIds: [...mergedProviderIds] }
        : {}),
    }
    this.stagedAssistantTurns.set(turn.providerEventId, {
      turn,
      responseId: event.response_id,
    })
    this.flushStartedAssistantTurns()
    this.flushNoAudioAssistantTurns()
  }

  private settleAssistantItem(itemId: string) {
    this.stagedAssistantTurns.delete(itemId)
    this.terminalAssistantItemIds.delete(itemId)
    this.settledAssistantItemIds.add(itemId)
  }

  // A started audio buffer authorizes persistence, but preserve playback order
  // when an earlier response stopped before its terminal transcript arrived.
  private flushStartedAssistantTurns() {
    let waitingForEarlierTranscript = false

    voiceLiveItemEntries(this.deps.getVoiceState()).forEach(({ item }) => {
      if (item.role !== "assistant") return
      if (
        this.settledAssistantItemIds.has(item.itemId) ||
        this.interruptedAssistantItemIds.has(item.itemId) ||
        this.clearedAssistantItemIds.has(item.itemId)
      ) {
        return
      }

      const audioState = item.responseId
        ? this.responseAudioStates.get(item.responseId)
        : undefined
      if (audioState !== "started" && audioState !== "stopped") return

      const stagedTurn = this.stagedAssistantTurns.get(item.itemId)
      if (!stagedTurn) {
        waitingForEarlierTranscript = true
        return
      }
      if (waitingForEarlierTranscript) return

      if (audioState === "started") {
        this.enqueueAssistantTurn(stagedTurn.turn)
      } else {
        this.persistAssistantTurn(stagedTurn.turn)
      }
    })
  }

  // A response cancelled before its audio buffer started was never heard: the
  // customer's resumed speech cut it off mid-generation. Left open, the next
  // response's start snaps its text fully revealed and teardown saves it as
  // heard, stamped after everything said since.
  private dropUnheardAssistantTurns(
    responseId: string,
    state: VoiceSessionStateSnapshot
  ) {
    voiceLiveItemEntries(state).forEach(({ item }) => {
      if (
        item.role !== "assistant" ||
        item.responseId !== responseId ||
        this.settledAssistantItemIds.has(item.itemId)
      ) {
        return
      }
      this.interruptedAssistantItemIds.add(item.itemId)
      this.settleAssistantItem(item.itemId)
      this.deps.onTurnTransition({
        kind: "turnInterrupted",
        id: item.itemId,
        audioEndMs: 0,
      })
    })
  }

  // A terminal response with no provider audio-content evidence owns the
  // no-audio path. Do not persist it while another buffer or clear lifecycle is
  // unresolved; interruption owns that competing terminal path.
  private flushNoAudioAssistantTurns() {
    if (this.audioBufferActive || this.clearedAssistantItemIds.size > 0) {
      return
    }

    const assistantItems = voiceLiveItemEntries(this.deps.getVoiceState())
      .map(({ item }) => item)
      .filter((item) => item.role === "assistant")

    Array.from(this.stagedAssistantTurns.values())
      .filter(
        (stagedTurn) =>
          this.responseAudioStates.get(stagedTurn.responseId) === "no_audio"
      )
      .filter((stagedTurn) => {
        const itemIndex = assistantItems.findIndex(
          (item) => item.itemId === stagedTurn.turn.providerEventId
        )
        return !assistantItems.slice(0, itemIndex).some((item) => {
          const audioState = item.responseId
            ? this.responseAudioStates.get(item.responseId)
            : undefined
          return (
            (audioState === "started" || audioState === "stopped") &&
            !this.settledAssistantItemIds.has(item.itemId) &&
            !this.interruptedAssistantItemIds.has(item.itemId) &&
            !this.clearedAssistantItemIds.has(item.itemId) &&
            !this.stagedAssistantTurns.has(item.itemId)
          )
        })
      })
      .forEach((stagedTurn) => this.persistAssistantTurn(stagedTurn.turn))
  }

  private persistAssistantTurn(turn: VoiceFinalizedTranscriptTurn) {
    this.settleAssistantItem(turn.providerEventId)
    this.enqueueAssistantTurn(turn)
  }

  private enqueueAssistantTurn(turn: VoiceFinalizedTranscriptTurn) {
    if (!this.openaiCallId) return

    persistTrace.info("finalized_persist", {
      providerEventId: turn.providerEventId,
      role: turn.role,
      interrupted: false,
      transcriptLength: turn.transcript.length,
      // The turn's link to the searches it narrated. Empty here is why a
      // paused turn's places silently failed to resolve — worth seeing in
      // the trace rather than inferring from a payload byte count.
      toolCallIds: turn.toolCallIds ?? [],
    })
    // A resumed turn's pause already persisted an interrupted heard prefix
    // under this provider id; the finalized merged write must replace it, not
    // dedupe against it.
    const merged = this.mergedTurnProviderIds.has(turn.providerEventId)
    void this.persistQueue.enqueue(
      this.chatId,
      turn,
      this.openaiCallId,
      {},
      merged ? { replaceExisting: true } : {}
    )
    // The merged annotation's resolution must run against the merged body:
    // its item deferred resolved back at the FIRST write, so chain the wait
    // through the queue boundary that includes this write — the same pattern
    // the interrupted replacement uses.
    const annotationState = merged
      ? this.placeAnnotationStates.get(turn.providerEventId)
      : undefined
    if (annotationState) {
      const itemPersistence = annotationState.transcriptSave
      const persistQueueSettled = this.persistQueue.settled
      annotationState.transcriptSave = (async () => {
        const persistence = await itemPersistence
        await persistQueueSettled
        return persistence
      })()
    }
  }

  private persistUserTurn(
    event: Extract<
      VoiceRealtimeEvent,
      { type: "conversation.item.input_audio_transcription.completed" }
    >,
    updatedVoiceState: VoiceSessionStateSnapshot
  ) {
    const sessionOpenaiCallId = this.openaiCallId
    const interruption = this.interruptingUserItems.get(event.item_id)
    const interruptedAssistant = this.interruptingUserItems.delete(
      event.item_id
    )
    const finalizedTurn = finalizedTranscriptTurnForRealtimeEvent(
      event,
      updatedVoiceState
    )
    if (!sessionOpenaiCallId) return
    if (!finalizedTurn) {
      this.gate.userTurnDiscarded()
      persistTrace.info("user_item_empty_transcript_skipped", {
        itemId: event.item_id,
      })
      // A barge-in that transcribed to nothing is noise/echo, not the user.
      // It already cancelled the assistant's reply; if the server isn't
      // speaking again, nudge a response instead of sitting in dead air.
      if (interruptedAssistant) {
        this.gate.requestPhantomInterruptResume()
      }
      if (interruption?.assistantAudioWasAudible) {
        this.deps.onPhantomBargeIn?.()
      }
      return
    }

    const persistence = this.persistQueue.enqueue(
      this.chatId,
      finalizedTurn,
      sessionOpenaiCallId
    )
    void persistence
    persistTrace.info("user_item_transcript_persist_enqueued", {
      itemId: finalizedTurn.providerEventId,
      transcriptLength: finalizedTurn.transcript.length,
    })
  }

  // Returns the heard prefix of the truncated item, empty when nothing played.
  private persistInterruptedAssistantTurn(
    event: Extract<VoiceRealtimeEvent, { type: "conversation.item.truncated" }>,
    updatedVoiceState: VoiceSessionStateSnapshot,
    fullTranscriptBeforeTruncation: string | null
  ): string {
    let heardTranscript = ""
    if (this.openaiCallId) {
      const cutoffReason: VoiceCutoffReason =
        this.cutReason ?? "user_interruption"
      pipelineTrace.info("assistant_interrupt_server_truncated", {
        itemId: event.item_id,
        audioEndMs: event.audio_end_ms,
        providerEventId: event.item_id,
      })
      const interruptedItem = voiceLiveItemEntries(updatedVoiceState).find(
        (entry) =>
          entry.item.itemId === event.item_id && entry.item.role === "assistant"
      )?.item
      heardTranscript = interruptedItem
        ? liveItemVisibleText(interruptedItem)
        : ""

      if (
        !this.deps.persistInitialResponse &&
        interruptedItem &&
        this.greetingResponseId !== null &&
        interruptedItem.responseId === this.greetingResponseId
      ) {
        persistTrace.info("greeting_persist_skipped", {
          providerEventId: interruptedItem.itemId,
          interrupted: true,
        })
        this.cutReason = null
        return heardTranscript
      }

      if (interruptedItem) {
        const toolCallIds = this.toolCallIdsForAssistantItem(
          interruptedItem.itemId,
          updatedVoiceState,
          heardTranscript
        )
        persistTrace.info("truncated_persist", {
          providerEventId: interruptedItem.itemId,
          role: "assistant",
          interrupted: true,
          transcriptLength: heardTranscript.length,
          fullTranscriptLength:
            fullTranscriptBeforeTruncation?.length ??
            interruptedItem.text.length,
          truncatedAtMs: event.audio_end_ms,
          cutoffReason,
          toolCallIds: toolCallIds ?? [],
          state: voiceRealtimeDebugState(updatedVoiceState),
        })
        void this.persistQueue.enqueue(
          this.chatId,
          {
            role: "assistant",
            transcript: heardTranscript,
            providerEventId: interruptedItem.itemId,
            toolCallIds,
            interrupted: true,
          },
          this.openaiCallId,
          {
            fullTranscript:
              fullTranscriptBeforeTruncation ?? interruptedItem.text,
            truncatedAtMs: event.audio_end_ms,
            cutoffReason,
          },
          {
            replaceExisting: true,
          }
        )
        const annotationState = this.placeAnnotationStates.get(
          interruptedItem.itemId
        )
        if (annotationState) {
          const itemPersistence = annotationState.transcriptSave
          const persistQueueSettled = this.persistQueue.settled
          annotationState.transcriptSave = (async () => {
            const persistence = await itemPersistence
            await persistQueueSettled
            return persistence
          })()
        }
      }
      this.cutReason = null
    }
    return heardTranscript
  }

  sendLocationUpdate(hit: LocationSearchHit): boolean {
    const area = [hit.name, hit.region, hit.country].filter(Boolean).join(", ")
    const sent = this.send({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "system",
        content: [
          {
            type: "input_text",
            text: `The customer's device position is now available to nearby searches. Its resolved area is ${area}, which supersedes any earlier approximate current-location city.`,
          },
        ],
      },
    })
    if (sent) {
      transportTrace.info("location_update_sent", { entityRef: hit.entity_ref })
    }
    return sent
  }

  private sendCutOffNote(heardTranscript: string) {
    this.send({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "system",
        content: [
          {
            type: "input_text",
            text: cutOffNoteText(heardTranscript),
          },
        ],
      },
    })
  }

  // Makes the assistant speak first once the session is up — the bounded
  // interview's opener. Voice chat no longer greets: it opens with a start
  // sound and `replayHistory()`. Awaits the data channel internally and fires
  // once.
  //
  // Realtime snapshots the conversation when generation starts. Send the
  // greeting first, then insert the boundary and whole turns newest-first at
  // root so every partial replay is a recent suffix ending at the boundary.
  // Items inside a turn stay forward-ordered because Realtime rejects a tool
  // output whose function call is not yet present.
  async sendGreeting() {
    if (this.closed || this.greetingSent) return
    this.greetingSent = true
    await this.dataChannelOpen
    this.sendGreetingResponse()
    this.sendHistoryItems()
  }

  // Publishes the prior turns into a session that will not introduce itself:
  // a voice chat opening, or a rebuild after the transport died. `greetingSent`
  // stays false on purpose: it tags the first user-less response as the hidden
  // greeting, and here that response is a real answer.
  async replayHistory() {
    if (this.closed) return
    await this.dataChannelOpen
    this.sendHistoryItems()
  }

  private sendGreetingResponse() {
    // The customer beat us to it — they started talking during connect, so the
    // ice-breaker is redundant. Skip it rather than talk over their first turn.
    if (this.closed) return
    if (this.greetingSuperseded) {
      this.releaseInitialResponseMic("greeting_skipped")
      return
    }
    this.send({
      type: "response.create",
      event_id: "voice_greeting",
      response: {
        metadata: { mindtrip_trigger: "greeting" },
      },
    })
  }

  private sendHistoryItems() {
    const { events, collapsedBytes, mode, chunkCount } =
      this.prepareHistoryEvents()
    const eventBytes = events.map(jsonBytes)
    const historyBytes = eventBytes.reduce((total, bytes) => total + bytes, 0)
    transportTrace.info("history_transport_prepared", {
      mode,
      collapsedBytes,
      historyBytes,
      historyItemCount: events.length,
      chunkCount,
      maxMessageSize: this.historyReplay.maxMessageSize,
    })

    for (const [index, event] of events.entries()) {
      const bytes = eventBytes[index]
      if (
        this.historyReplay.maxMessageSize !== Infinity &&
        this.historyReplay.maxMessageSize !== null &&
        bytes > this.historyReplay.maxMessageSize
      ) {
        transportTrace.warn("history_item_exceeds_sctp_limit", {
          index,
          itemId: event.item.id,
          bytes,
          maxMessageSize: this.historyReplay.maxMessageSize,
        })
      }
      if (!this.send(event)) {
        transportTrace.warn("history_send_failed", {
          index,
          itemId: event.item.id,
        })
        return
      }
    }

    const finalItemId = events.at(-1)?.item.id
    if (!finalItemId) {
      transportTrace.info("history_ready", {
        elapsedMs: 0,
        ackItemId: null,
        historyBytes: 0,
        historyItemCount: 0,
        mode,
      })
      return
    }

    const startedAt = performance.now()
    const timer = setTimeout(
      () => this.historyAcknowledgementTimedOut(),
      10_000
    )
    this.historyReplay.acknowledgement = {
      itemId: finalItemId,
      timer,
      startedAt,
      bytes: historyBytes,
      itemCount: events.length,
      mode,
    }
  }

  private prepareHistoryEvents() {
    const turns: VoiceHistoryItem[][] = []
    for (const item of this.historyItems) {
      const startsTurn =
        turns.length === 0 ||
        (item.type === "message" && item.role !== "assistant")
      if (startsTurn) {
        turns.push([])
      }
      turns[turns.length - 1].push(item)
    }

    const events: ReturnType<typeof historyEvent>[] = []
    for (const turn of turns.reverse()) {
      const items = this.chunkCollapsedHistoryItem(turn, events.length)
      items.forEach((item, offset) => {
        events.push(historyEvent(item, events.length, offset === 0))
      })
    }

    const collapsedItem = this.historyItems.find(isCollapsedHistoryItem)
    const summary = historyTransportSummary(
      collapsedItem,
      events.length,
      this.historyItems.length
    )
    return {
      events,
      ...summary,
    }
  }

  private chunkCollapsedHistoryItem(
    items: VoiceHistoryItem[],
    firstEventIndex: number
  ) {
    const collapsed = items.find(isCollapsedHistoryItem)
    if (!collapsed) {
      return items
    }
    const collapsedIndex = items.indexOf(collapsed)
    const limit = this.historyReplay.maxMessageSize ?? Infinity
    if (limit === Infinity) {
      return items
    }

    // VoiceRealtimePrompt::COLLAPSED_HISTORY_START is the shared protocol token;
    // the client only chunks its text and never parses the JSON-line payload.
    const lines = collapsed.content[0].text.split("\n")
    const chunkItem = (text: string): VoiceHistoryItem => ({
      type: "message",
      role: "user",
      content: [{ type: "input_text", text }],
    })
    // Measured in the largest envelope a chunk can get: a forward-chained
    // previous_item_id and an index at least as long as any chunk's real one.
    const fits = (text: string) =>
      jsonBytes(
        historyEvent(chunkItem(text), firstEventIndex + lines.length, false)
      ) <= limit
    const chunks: string[] = []
    let chunk = ""
    for (const line of lines) {
      const candidate = chunk ? `${chunk}\n${line}` : line
      if (fits(candidate)) {
        chunk = candidate
        continue
      }
      // A single line over the limit cannot be split; send the item whole and
      // let sendHistoryItems warn about it.
      if (!fits(line)) return items
      chunks.push(chunk)
      chunk = line
    }
    chunks.push(chunk)
    return [
      ...items.slice(0, collapsedIndex),
      ...chunks.map(chunkItem),
      ...items.slice(collapsedIndex + 1),
    ]
  }

  private acknowledgeHistoryItem(itemId: string) {
    const acknowledgement = this.historyReplay.acknowledgement
    if (acknowledgement?.itemId !== itemId) {
      return
    }
    clearTimeout(acknowledgement.timer)
    transportTrace.info("history_ready", {
      elapsedMs: performance.now() - acknowledgement.startedAt,
      ackItemId: itemId,
      historyBytes: acknowledgement.bytes,
      historyItemCount: acknowledgement.itemCount,
      mode: acknowledgement.mode,
    })
    this.historyReplay.acknowledgement = null
  }

  private historyAcknowledgementTimedOut() {
    const acknowledgement = this.historyReplay.acknowledgement
    if (!acknowledgement) {
      return
    }
    transportTrace.warn("history_ack_failed", {
      itemId: acknowledgement.itemId,
      reason: "timeout",
    })
    transportTrace.info("history_ready", {
      elapsedMs: performance.now() - acknowledgement.startedAt,
      ackItemId: acknowledgement.itemId,
      historyBytes: acknowledgement.bytes,
      historyItemCount: acknowledgement.itemCount,
      mode: acknowledgement.mode,
      timedOut: true,
    })
    this.historyReplay.acknowledgement = null
  }

  // The greeting is the first response of the session (fired before any user
  // turn), so we tag its response id. Callers choose whether items under it are
  // ephemeral or use the normal audible persistence path.
  private isGreetingTurn(
    itemId: string,
    state: VoiceSessionStateSnapshot
  ): boolean {
    if (this.greetingResponseId === null) return false
    const item = voiceLiveItemEntries(state).find(
      (entry) => entry.item.itemId === itemId
    )?.item
    return item?.responseId === this.greetingResponseId
  }

  private flushUnpersistedAssistantTurns(
    beforeStopState: VoiceSessionStateSnapshot
  ) {
    const selectedChatId = beforeStopState.selectedChatId
    const openaiCallId = this.openaiCallId
    if (!selectedChatId || !openaiCallId) return

    const assistantItems = beforeStopState.liveStream.filter(
      (entry): entry is Extract<VoiceLiveEntry, { kind: "item" }> =>
        entry.kind === "item" && entry.item.role === "assistant"
    )

    Array.from(this.stagedAssistantTurns.values()).forEach((stagedTurn) => {
      const { turn, responseId } = stagedTurn
      if (this.settledAssistantItemIds.has(turn.providerEventId)) return

      const item = assistantItems.find(
        (entry) => entry.item.itemId === turn.providerEventId
      )?.item
      if (
        !this.deps.persistInitialResponse &&
        this.greetingResponseId !== null &&
        responseId === this.greetingResponseId
      ) {
        this.settleAssistantItem(turn.providerEventId)
        return
      }

      const heardTranscript = item ? liveItemVisibleText(item) : ""
      if (this.clearedAssistantItemIds.has(turn.providerEventId)) {
        this.interruptedAssistantItemIds.add(turn.providerEventId)
        this.settleAssistantItem(turn.providerEventId)

        void this.persistQueue.enqueue(
          selectedChatId,
          {
            ...turn,
            transcript: heardTranscript,
            interrupted: true,
          },
          openaiCallId,
          {
            fullTranscript: turn.transcript,
            truncatedAtMs: null,
            cutoffReason: this.cutReason ?? "user_interruption",
          },
          {
            replaceExisting: true,
          }
        )
        return
      }

      if (heardTranscript.trim().length === 0) {
        this.persistAssistantTurn(turn)
        return
      }

      this.interruptedAssistantItemIds.add(turn.providerEventId)
      this.settleAssistantItem(turn.providerEventId)
      void this.persistQueue.enqueue(
        selectedChatId,
        {
          ...turn,
          transcript: heardTranscript,
          interrupted: true,
        },
        openaiCallId,
        {
          fullTranscript: turn.transcript,
          truncatedAtMs: null,
          cutoffReason: "session_ended",
        },
        {
          replaceExisting: true,
        }
      )
    })

    assistantItems.forEach(({ item }) => {
      const heardTranscript = liveItemVisibleText(item)
      if (
        item.messageId ||
        this.settledAssistantItemIds.has(item.itemId) ||
        heardTranscript.trim().length === 0 ||
        (!this.deps.persistInitialResponse &&
          this.greetingResponseId !== null &&
          item.responseId === this.greetingResponseId)
      ) {
        return
      }

      this.interruptedAssistantItemIds.add(item.itemId)
      this.settleAssistantItem(item.itemId)
      void this.persistQueue.enqueue(
        selectedChatId,
        {
          role: "assistant",
          transcript: heardTranscript,
          providerEventId: item.itemId,
          toolCallIds: this.toolCallIdsForAssistantItem(
            item.itemId,
            beforeStopState,
            heardTranscript
          ),
          interrupted: true,
        },
        openaiCallId,
        {
          fullTranscript: item.text,
          truncatedAtMs: null,
          cutoffReason: this.clearedAssistantItemIds.has(item.itemId)
            ? (this.cutReason ?? "user_interruption")
            : "session_ended",
        },
        {
          replaceExisting: true,
        }
      )
    })
  }

  private clearAssistantFinalizationState() {
    this.stagedAssistantTurns.clear()
    this.terminalAssistantItemIds.clear()
    this.responseAudioStates.clear()
    this.clearedAssistantItemIds.clear()
    this.interruptedAssistantItemIds.clear()
    this.settledAssistantItemIds.clear()
    this.audioBufferActive = false
    this.pausedRemainder = null
    this.pauseContinuation = null
    this.continuationItemAliases.clear()
    this.mergedTurnProviderIds.clear()
    this.foldedSuccessorItemIds.clear()
    this.pendingPauseTruncationItemId = null
  }

  private joinGraphqlBatch(responseId: string, entry: GraphqlBatchEntry) {
    const pending = this.pendingGraphqlBatches.get(responseId)
    if (pending) {
      pending.entries.push(entry)
      return pending
    }

    const batch: GraphqlBatch = {
      entries: [entry],
      resolve: () => undefined,
      reject: () => undefined,
      results: Promise.resolve(new Map()),
    }
    batch.results = new Promise<Map<string, VoiceToolResult>>(
      (resolve, reject) => {
        batch.resolve = resolve
        batch.reject = reject
      }
    )
    // A waiter that returned early (session closed) leaves nobody observing
    // this promise; keep a rejection from surfacing as an unhandled one.
    void observeRejection(batch.results)
    this.pendingGraphqlBatches.set(responseId, batch)
    return batch
  }

  // `response.done` closes the response's batch. A cancelled response sends
  // nothing and tells the model to re-issue; a completed one dispatches the
  // batch through the edit chain.
  private closeGraphqlBatch(responseId: string, completed: boolean) {
    const batch = this.pendingGraphqlBatches.get(responseId)
    if (!batch) return
    this.pendingGraphqlBatches.delete(responseId)

    if (!completed) {
      batch.resolve(
        new Map(
          batch.entries.map((entry) => [
            entry.callId,
            CANCELLED_TOOL_CALL_RESULT,
          ])
        )
      )
      return
    }

    void this.settleGraphqlBatch(responseId, batch)
  }

  private async settleGraphqlBatch(responseId: string, batch: GraphqlBatch) {
    try {
      batch.resolve(
        await this.chainEdit(() =>
          this.dispatchGraphqlBatch(responseId, batch.entries)
        )
      )
    } catch (error) {
      batch.reject(error)
    }
  }

  private async dispatchGraphqlBatch(
    responseId: string,
    entries: GraphqlBatchEntry[]
  ) {
    const sessionOpenaiCallId = this.openaiCallId
    if (!sessionOpenaiCallId) {
      throw new Error("Voice session is missing an OpenAI call id.")
    }

    // Siblings of one response share the triggering customer turn, and each
    // waiter already awaits this same persistence promise.
    const userItem = await this.persistQueue.persistenceFor(
      entries[0].userItemId
    )
    if (userItem.deleted === true) {
      throw new Error("Voice tool requires a persisted user message.")
    }
    toolsTrace.info("voice_graphql_batch_dispatched", {
      responseId,
      callIds: entries.map((entry) => entry.callId),
    })
    const results = await this.deps.runGraphqlTools({
      chatId: this.chatId,
      openaiCallId: sessionOpenaiCallId,
      userMessageId: userItem.messageId,
      responseId,
      completedSkillCallIds: this.installedSkillSnapshot?.callIds,
      toolCalls: entries.map((entry) => ({
        callId: entry.callId,
        outputIndex: entry.outputIndex,
        arguments: entry.arguments,
      })),
    })
    return new Map(results.map(({ callId, result }) => [callId, result]))
  }

  private async graphqlBatchResult(batch: GraphqlBatch, callId: string) {
    const result = (await batch.results).get(callId)
    if (!result) {
      throw new Error("The GraphQL batch returned no result for this call.")
    }
    return result
  }

  // No `await` here, so the snapshot-and-reassign cannot be interleaved by a
  // concurrent edit; the waiting itself lives in dispatchAfter.
  private chainEdit<T>(dispatch: () => Promise<T>): Promise<T> {
    const result = this.dispatchAfter(this.editChain, dispatch)
    this.editChain = result
    // The chain keeps a handle on every edit; the caller owns the rejection.
    void observeRejection(result)
    return result
  }

  private async dispatchAfter<T>(
    previous: Promise<unknown>,
    dispatch: () => Promise<T>
  ): Promise<T> {
    try {
      await previous
    } catch {
      // A failed edit must not block the next one.
    }
    return dispatch()
  }

  private responseIdForToolEvent(
    event: Extract<
      VoiceRealtimeEvent,
      { type: "response.function_call_arguments.done" }
    >
  ) {
    const responseId = event.response_id ?? this.gate.activeResponseId
    if (!responseId) {
      throw new Error("Voice tool call requires a provider response id.")
    }
    return responseId
  }

  private async handleToolCall(
    event: Extract<
      VoiceRealtimeEvent,
      { type: "response.function_call_arguments.done" }
    >,
    userItemId: string | null
  ) {
    const eventSeq = currentTraceEventSeq()
    const eventToolsTrace = voiceTracer(
      "tools",
      { callId: event.call_id, toolName: event.name },
      eventSeq
    )
    const eventPersistTrace = voiceTracer(
      "persist",
      { callId: event.call_id, toolName: event.name },
      eventSeq
    )
    // Stamped now, not at delivery: a long search's result must be batched
    // with the turn that asked for it, however many turns pass before it lands.
    const customerSpeechGeneration = this.gate.currentCustomerSpeechGeneration
    try {
      let toolArguments: Record<string, unknown>
      try {
        toolArguments = toolArgumentsFromRealtimeEvent(event.arguments)
      } catch (error) {
        eventToolsTrace.warn("voice_tool_arguments_parse_failed", {
          argumentLength: event.arguments.length,
          message: errorMessage(error, "Could not parse tool arguments."),
        })
        // Unparseable arguments mean the response was cancelled mid-stream
        // (usually a VAD barge-in truncating the JSON), not a tool failure:
        // skip the failed-tool card and tell the model to re-issue the call.
        void this.toolResultRelay.deliver(
          {
            callId: event.call_id,
            toolName: event.name,
            customerSpeechGeneration,
            result: CANCELLED_TOOL_CALL_RESULT,
          },
          this.openaiCallId
        )
        eventToolsTrace.info("voice_tool_final_payload_sent", {
          payloadType: "cancelled_call_retry_hint",
        })
        return
      }
      const sessionOpenaiCallId = this.openaiCallId
      if (!sessionOpenaiCallId) {
        throw new Error("Voice session is missing an OpenAI call id.")
      }

      if (!userItemId) {
        throw new Error("Voice tool call requires a triggering user item.")
      }

      // Joined synchronously, before the persistence wait: a slow transcript
      // write can outlast `response.done`, and a call that joined afterwards
      // would open a second batch nothing ever closes.
      const batch =
        event.name === GRAPHQL_QUERY_TOOL_NAME
          ? this.joinGraphqlBatch(this.responseIdForToolEvent(event), {
              callId: event.call_id,
              outputIndex: event.output_index,
              arguments: toolArguments,
              userItemId,
            })
          : null

      eventPersistTrace.info("voice_tool_persistence_wait_started", {
        itemId: userItemId,
      })
      const userItem = await this.persistQueue.persistenceFor(userItemId)
      if (this.closed) return

      eventPersistTrace.info("voice_tool_persistence_wait_succeeded", {
        itemId: userItemId,
        messageId: userItem.messageId,
      })
      if (userItem.deleted === true) {
        throw new Error("Voice tool requires a persisted user message.")
      }
      this.deps.onToolStarted({
        callId: event.call_id,
        toolName: event.name,
        progressLabel: toolCallProgressLabel(
          event.name,
          {
            ...this.toolProgressLabels,
            ...this.installedSkillSnapshot?.toolProgressLabels,
          },
          toolArguments,
          this.installedSkillSnapshot?.graphqlProgressLabels
        ),
        query: toolArgumentsLabel(event.name, toolArguments),
        locationHint: toolLocationLabel(toolArguments),
      })

      eventToolsTrace.info("voice_tool_run_started", {
        arguments: toolArguments,
      })
      const runSingularTool = () =>
        this.deps.runTool({
          chatId: this.chatId,
          openaiCallId: sessionOpenaiCallId,
          userMessageId: userItem.messageId,
          callId: event.call_id,
          toolName: event.name,
          arguments: toolArguments,
          completedSkillCallIds: this.installedSkillSnapshot?.callIds,
        })
      let result: VoiceToolResult
      if (batch) {
        result = await this.graphqlBatchResult(batch, event.call_id)
      } else if (event.name === ITINERARY_UNDO_REDO_TOOL_NAME) {
        result = await this.chainEdit(runSingularTool)
      } else {
        result = await runSingularTool()
      }
      if (this.closed) return

      eventToolsTrace.info("voice_tool_run_returned", {
        resultType:
          "status" in result && result.status === "running"
            ? "running"
            : "immediate",
        handle:
          "status" in result && result.status === "running"
            ? result.handle
            : undefined,
      })

      if ("status" in result && result.status === "running") {
        const pending = this.toolRunner.registerPending({
          chatId: this.chatId,
          openaiCallId: sessionOpenaiCallId,
          customerSpeechGeneration,
          callId: event.call_id,
          toolName: event.name,
          handle: result.handle,
        })
        void this.toolRunner.fetchPending(pending, "immediate")
        return
      }

      if (this.closed) return

      void this.toolRunner.deliver(
        {
          callId: event.call_id,
          toolName: event.name,
          customerSpeechGeneration,
          result,
        },
        null,
        "immediate",
        eventSeq
      )
    } catch (error) {
      if (this.closed) return

      const message = errorMessage(error, "Could not run tool.")
      eventPersistTrace.warn("voice_tool_persistence_wait_failed", {
        itemId: userItemId,
        message,
      })
      eventToolsTrace.warn("voice_tool_backend_error", {
        message,
      })
      this.deps.onToolFailed({
        callId: event.call_id,
        toolName: event.name,
        message,
      })
      void this.toolResultRelay.deliver(
        {
          callId: event.call_id,
          toolName: event.name,
          customerSpeechGeneration,
          result: { error: message, isError: true },
        },
        this.openaiCallId
      )
      eventToolsTrace.info("voice_tool_final_payload_sent", {
        payloadType: "local_error",
      })
    }
  }

  // Tool-result ownership attaches once, to the first nonblank assistant item
  // in the correlated response. A response can contain multiple streamed items,
  // but duplicating call ids across their persisted turns would duplicate the
  // structured result in chat.
  private responseCreated(
    responseId: string,
    latestUserItemId: string | null,
    trigger?: VoiceResponseCreateTrigger
  ) {
    const toolCallIds = this.gate.responseCreated(responseId, trigger)
    if (this.gate.isResponseSuppressed(responseId)) return true

    if (toolCallIds.length > 0) {
      this.responseToolOwnership.set(responseId, {
        callIds: toolCallIds,
        ownerItemId: null,
      })
    }
    if (latestUserItemId) {
      this.responseUserItemIds[responseId] = latestUserItemId
    }
    return false
  }

  private userItemIdForToolEvent(
    event: Extract<
      VoiceRealtimeEvent,
      { type: "response.function_call_arguments.done" }
    >,
    latestUserItemId: string | null
  ) {
    const responseId = event.response_id ?? this.gate.activeResponseId
    if (responseId) {
      const itemId = this.responseUserItemIds[responseId]
      if (itemId) return itemId
    }
    return latestUserItemId
  }

  private assistantRevealInProgress(state: VoiceSessionStateSnapshot) {
    return voiceLiveItemEntries(state).some(
      (entry) =>
        entry.item.role === "assistant" &&
        entry.item.revealedLength < entry.item.text.length
    )
  }

  private outputSpeedSessionUpdateReady(state: VoiceSessionStateSnapshot) {
    return (
      state.connectionStatus !== "idle" &&
      state.connectionStatus !== "connecting" &&
      state.connectionStatus !== "assistant_speaking" &&
      !this.gate.activeResponseId &&
      !this.assistantRevealInProgress(state)
    )
  }

  private latestUserItemId(state: VoiceSessionStateSnapshot) {
    const entries = voiceLiveItemEntries(state)
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const item = entries[index].item
      if (item.role === "user") return item.itemId
    }
    return null
  }
}
